import { WorkerBus } from './WorkerBus'
import { isFuseGridNativeDesktopShell } from './desktop/isDesktop'
import { tableFromIPC } from 'apache-arrow'
import type { WorksheetPlannerMetadata } from '../tableau/worksheetPlannerMetadata'
import { buildCanonicalRootIdentityKey } from '../tableau/datasourceExecutionGraph'
import { ensureTabExecutionLeaseForScope } from './browserTabExecutionIdentity'
import {
  buildSpreadsheetEngineSheetId,
  buildSpreadsheetOperationEnvelope,
  buildSpreadsheetWorkerCommand,
  getSpreadsheetRoutedResponseRequestId,
  isSpreadsheetDeltaResponsePayload,
  isSpreadsheetExcelExportResponsePayload,
  isSpreadsheetExcelExportSnapshotPayload,
  isSpreadsheetExcelImportPreviewResponsePayload,
  isSpreadsheetExcelImportResponsePayload,
  isSpreadsheetRestoreWorkbookSnapshotResponsePayload,
  isSpreadsheetSheetListResponsePayload,
  isSpreadsheetWorkbookSnapshotResponsePayload,
  type SpreadsheetDeltaResponsePayload,
  type SpreadsheetEngineOperationEnvelope,
  type SpreadsheetEngineWorkerCommand,
  type SpreadsheetEngineWorkerResponseName,
  type SpreadsheetExcelExportResponsePayload,
  type SpreadsheetExcelExportSnapshotPayload,
  type SpreadsheetExcelExportWorkbookPayload,
  type SpreadsheetExcelImportPreviewPayload,
  type SpreadsheetExcelImportPreviewResponsePayload,
  type SpreadsheetExcelImportResponsePayload,
  type SpreadsheetExcelImportWorkbookPayload,
  type SpreadsheetRestoreWorkbookSnapshotResponsePayload,
  type SpreadsheetSheetListResponsePayload,
  type SpreadsheetWorkbookSnapshotResponsePayload,
  type SpreadsheetWorkbookSnapshot,
} from '../protocol/spreadsheetEngineProtocol'
import { BROWSER_DURABLE_CACHE_BUDGET_LOCALSTORAGE_KEY } from '../worker/browserDurableCacheMaintenance'

export const CFO_CORE_DUCKDB_DATASET_ID = '__fg_cfo_core_duckdb__'

type SpreadsheetSheetMetadataOperationPayload = {
  name?: string
  index?: number
  row_count?: number
  col_count?: number
  hidden?: boolean
  protection?: Record<string, unknown> | null
  tab_color?: Record<string, unknown> | null
  source_sheet_id?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value))

const stripOuterPredicateParens = (value: string): string => {
  let s = String(value || '').trim()
  for (;;) {
    if (!s.startsWith('(') || !s.endsWith(')')) return s
    let depth = 0
    let wraps = true
    for (let i = 0; i < s.length; i += 1) {
      const ch = s[i]
      if (ch === '(') depth += 1
      else if (ch === ')') depth -= 1
      if (depth === 0 && i < s.length - 1) {
        wraps = false
        break
      }
      if (depth < 0) {
        wraps = false
        break
      }
    }
    if (!wraps || depth !== 0) return s
    s = s.slice(1, -1).trim()
  }
}

const isNonRestrictiveWhereSql = (whereSqlRaw?: string | null): boolean => {
  let sql = String(whereSqlRaw ?? '').trim()
  if (!sql) return true
  sql = sql.replace(/^where\b/i, '').trim()
  if (!sql) return true
  const parts = sql
    .split(/\s+AND\s+/i)
    .map((part) => stripOuterPredicateParens(part.replace(/\s+/g, ' ')))
    .filter(Boolean)
  if (parts.length <= 0) return true
  return parts.every((part) => /^(?:TRUE|1\s*=\s*1)$/i.test(part))
}

// --- Desktop debug: dump exact SQL to logs for copy/paste ---
//
// In the Tauri app, interactive authoring can produce complex SQL that we need to debug.
// This helper logs the *exact* SQL strings (and exec-plan statements) in a delimited block
// so they can be copy/pasted from the desktop logs.
const shouldDumpSqlToLogs = (): boolean => {
  if (typeof window === 'undefined') return false
  if (!isFuseGridNativeDesktopShell()) return false
  try {
    const truthy = (v: any) =>
      ['1', 'true', 'yes', 'on'].includes(
        String(v ?? '')
          .trim()
          .toLowerCase(),
      )
    const falsy = (v: any) =>
      ['0', 'false', 'no', 'off'].includes(
        String(v ?? '')
          .trim()
          .toLowerCase(),
      )
    const sp = new URLSearchParams(window.location.search)
    const raw = sp.get('fgDumpSql') || sp.get('fg_dump_sql') || sp.get('dump_sql') || ''
    // Explicit URL override takes precedence.
    if (raw) return truthy(raw) && !falsy(raw)
    const ls = window.localStorage.getItem('fg_dump_sql') || window.localStorage.getItem('fgDumpSql') || ''
    // Explicit localStorage override.
    if (ls && falsy(ls)) return false
    if (truthy(ls)) return true
  } catch {}
  // Default-off: dumping huge SQL blocks can destroy interactive performance.
  // Enable explicitly via query param or localStorage.
  try {
    return false
  } catch {
    return false
  }
}

let __fg_sql_dump_seq = 0
const logSqlDumpBlock = (label: string, body: string, meta?: any) => {
  try {
    const id = ++__fg_sql_dump_seq
    const metaJson = (() => {
      try {
        return JSON.stringify(meta ?? null)
      } catch {
        return 'null'
      }
    })()
    // Single console call with newlines: easier to copy/paste from Tauri logs.
    console.log(
      `[FG_SQL_DUMP] BEGIN id=${id} label=${label} meta=${metaJson}\n` + String(body ?? '') + `\n[FG_SQL_DUMP] END id=${id} label=${label}`,
    )
    // Also stash in a global ring buffer for easy retrieval via DevTools.
    try {
      const w = globalThis as any
      const arr = Array.isArray(w.__FG_SQL_DUMPS__) ? w.__FG_SQL_DUMPS__ : []
      arr.push({
        id,
        ts: new Date().toISOString(),
        label,
        meta: meta ?? null,
        body: String(body ?? ''),
      })
      w.__FG_SQL_DUMPS__ = arr.slice(-30)
    } catch {}
  } catch {}
}

/**
 * AppWorkerClient: Strongly-typed client for application RPC calls to the worker.
 *
 * Implements Phase 1.2 of COHESIVE_PHASED_PLAN.md
 */
export class AppWorkerClient {
  private bus: WorkerBus
  private defaultExecutionScopeId: string | null = null
  private readonly tableauWorksheetBuildInFlight = new Map<string, Promise<any>>()
  private static readonly directNativeSessionByDataset = new Map<string, string>()
  constructor(bus: WorkerBus) {
    this.bus = bus
    if (typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(BROWSER_DURABLE_CACHE_BUDGET_LOCALSTORAGE_KEY)
        if (raw) {
          void this.bus.request('cache_set_browser_durable_cache_budget', [{ budgetBytes: raw }], { timeoutMs: 10_000 }).catch(() => {})
        }
      } catch {}
    }
  }

  getWorkerBus(): WorkerBus {
    return this.bus
  }

  private requestSpreadsheetWorkerResponse<T>(
    commandName: string,
    message: SpreadsheetEngineWorkerCommand,
    responseName: SpreadsheetEngineWorkerResponseName,
    timeoutMs = 30000,
    responseGuard?: (payload: unknown) => payload is T,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const requestId = String(message?.requestId ?? '').trim()
      if (!requestId) {
        reject(new Error(`Spreadsheet worker ${commandName}: missing requestId`))
        return
      }
      let done = false
      let unsubscribeResponse: (() => void) | null = null
      let unsubscribeError: (() => void) | null = null
      const cleanup = () => {
        if (unsubscribeResponse) {
          try {
            unsubscribeResponse()
          } catch {}
          unsubscribeResponse = null
        }
        if (unsubscribeError) {
          try {
            unsubscribeError()
          } catch {}
          unsubscribeError = null
        }
      }
      const timer = setTimeout(
        () => {
          if (done) return
          done = true
          cleanup()
          reject(new Error(`Spreadsheet worker timeout for ${commandName}`))
        },
        Math.max(1000, Math.floor(Number(timeoutMs) || 30000)),
      )

      const finishResolve = (payload: unknown) => {
        if (done) return
        if (getSpreadsheetRoutedResponseRequestId(payload) !== requestId) return
        if (responseGuard && !responseGuard(payload)) {
          done = true
          clearTimeout(timer)
          cleanup()
          reject(new Error(`Malformed spreadsheet worker response for ${commandName}`))
          return
        }
        done = true
        clearTimeout(timer)
        cleanup()
        resolve(payload as T)
      }
      const finishReject = (payload: unknown) => {
        if (done) return
        if (getSpreadsheetRoutedResponseRequestId(payload) !== requestId) return
        done = true
        clearTimeout(timer)
        cleanup()
        const messageText = isRecord(payload)
          ? String(payload.message ?? `Spreadsheet worker error for ${commandName}`)
          : `Spreadsheet worker error for ${commandName}`
        reject(new Error(messageText))
      }

      unsubscribeResponse = this.bus.subscribe(responseName, finishResolve)
      unsubscribeError = this.bus.subscribe('error', finishReject)
      try {
        this.bus.sendCommand(commandName, message)
      } catch (error) {
        done = true
        clearTimeout(timer)
        cleanup()
        reject(error)
      }
    })
  }

  private async requestSpreadsheetSheetList(): Promise<SpreadsheetSheetListResponsePayload> {
    const command = buildSpreadsheetWorkerCommand({
      command: 'sheet.list',
      requestId: Math.random().toString(36).substring(7),
      workbookId: 'active_workbook',
      sheetId: null,
      baseRevision: 0,
      clientId: 'app_worker_client',
      payload: {},
    })
    return this.requestSpreadsheetWorkerResponse<SpreadsheetSheetListResponsePayload>(
      command.command,
      command,
      'sheet.list',
      30000,
      isSpreadsheetSheetListResponsePayload,
    )
  }

  async import_excel_workbook_preview(
    payload: SpreadsheetExcelImportPreviewPayload,
  ): Promise<SpreadsheetExcelImportPreviewResponsePayload> {
    const command = buildSpreadsheetWorkerCommand({
      command: 'excel.importPreview',
      requestId: Math.random().toString(36).substring(7),
      workbookId: 'active_workbook',
      sheetId: null,
      baseRevision: 0,
      clientId: 'app_worker_client',
      payload,
    })
    return this.requestSpreadsheetWorkerResponse<SpreadsheetExcelImportPreviewResponsePayload>(
      command.command,
      command,
      'excel.importPreviewed',
      60000,
      isSpreadsheetExcelImportPreviewResponsePayload,
    )
  }

  async import_excel_workbook(payload: SpreadsheetExcelImportWorkbookPayload): Promise<SpreadsheetExcelImportResponsePayload> {
    const command = buildSpreadsheetWorkerCommand({
      command: 'excel.importWorkbook',
      requestId: Math.random().toString(36).substring(7),
      workbookId: payload.target_workbook_id || 'active_workbook',
      sheetId: null,
      baseRevision: 0,
      clientId: payload.actor_client_id || 'app_worker_client',
      payload,
    })
    return this.requestSpreadsheetWorkerResponse<SpreadsheetExcelImportResponsePayload>(
      command.command,
      command,
      'excel.imported',
      60000,
      isSpreadsheetExcelImportResponsePayload,
    )
  }

  async export_excel_workbook(payload: SpreadsheetExcelExportWorkbookPayload): Promise<SpreadsheetExcelExportResponsePayload> {
    const command = buildSpreadsheetWorkerCommand({
      command: 'excel.exportWorkbook',
      requestId: Math.random().toString(36).substring(7),
      workbookId: 'active_workbook',
      sheetId: null,
      baseRevision: 0,
      clientId: payload.actor_client_id || 'app_worker_client',
      payload,
    })
    return this.requestSpreadsheetWorkerResponse<SpreadsheetExcelExportResponsePayload>(
      command.command,
      command,
      'excel.exported',
      60000,
      isSpreadsheetExcelExportResponsePayload,
    )
  }

  async export_excel_snapshot(payload: SpreadsheetExcelExportSnapshotPayload): Promise<SpreadsheetExcelExportResponsePayload> {
    if (!isSpreadsheetExcelExportSnapshotPayload(payload)) {
      throw new Error('Malformed Excel snapshot export payload')
    }
    const command = buildSpreadsheetWorkerCommand({
      command: 'excel.exportSnapshot',
      requestId: Math.random().toString(36).substring(7),
      workbookId: payload.snapshot.workbook_id || 'snapshot_workbook',
      sheetId: null,
      baseRevision: payload.snapshot.workbook_revision || 0,
      clientId: payload.actor_client_id || 'app_worker_client',
      payload,
    })
    return this.requestSpreadsheetWorkerResponse<SpreadsheetExcelExportResponsePayload>(
      command.command,
      command,
      'excel.exported',
      60000,
      isSpreadsheetExcelExportResponsePayload,
    )
  }

  async restore_spreadsheet_workbook_snapshot(
    snapshot: SpreadsheetWorkbookSnapshot,
  ): Promise<SpreadsheetRestoreWorkbookSnapshotResponsePayload> {
    const command = buildSpreadsheetWorkerCommand({
      command: 'workbook.restoreSnapshot',
      requestId: Math.random().toString(36).substring(7),
      workbookId: snapshot.workbook_id || 'active_workbook',
      sheetId: null,
      baseRevision: Math.max(0, Math.floor(Number(snapshot.workbook_revision ?? 0) || 0)),
      clientId: 'app_worker_client',
      payload: {
        snapshot,
        actor_client_id: 'app_worker_client',
        created_at_ms: Date.now(),
      },
    })
    return this.requestSpreadsheetWorkerResponse<SpreadsheetRestoreWorkbookSnapshotResponsePayload>(
      command.command,
      command,
      'workbook.restored',
      60000,
      isSpreadsheetRestoreWorkbookSnapshotResponsePayload,
    )
  }

  async get_spreadsheet_workbook_snapshot(): Promise<SpreadsheetWorkbookSnapshotResponsePayload> {
    const command = buildSpreadsheetWorkerCommand({
      command: 'workbook.snapshot',
      requestId: Math.random().toString(36).substring(7),
      workbookId: 'active_workbook',
      sheetId: null,
      baseRevision: 0,
      clientId: 'app_worker_client',
      payload: {},
    })
    return this.requestSpreadsheetWorkerResponse<SpreadsheetWorkbookSnapshotResponsePayload>(
      command.command,
      command,
      'workbook.snapshot',
      60000,
      isSpreadsheetWorkbookSnapshotResponsePayload,
    )
  }

  private async submitSpreadsheetSheetMetadataOperation(
    opType:
      | 'AddSheet'
      | 'DuplicateSheet'
      | 'RenameSheet'
      | 'DeleteSheet'
      | 'MoveSheet'
      | 'SetSheetHidden'
      | 'SetSheetTabColor'
      | 'SetSheetProtection',
    sheetIdNumber: number,
    payload: SpreadsheetSheetMetadataOperationPayload,
  ): Promise<SpreadsheetEngineOperationEnvelope> {
    const requestId = Math.random().toString(36).substring(7)
    const sheetId = buildSpreadsheetEngineSheetId(sheetIdNumber)
    const operation = buildSpreadsheetOperationEnvelope({
      op_id: `app_worker_client_${opType}_${Date.now()}_${requestId}`,
      op_type: opType,
      workbook_id: 'active_workbook',
      sheet_id: sheetId,
      base_revision: 0,
      actor_client_id: 'app_worker_client',
      created_at_ms: Date.now(),
      payload: {
        ...payload,
        sheet_id: sheetId,
      },
    })
    const command = buildSpreadsheetWorkerCommand({
      command: 'sheet.applyOps',
      requestId,
      workbookId: 'active_workbook',
      sheetId,
      baseRevision: 0,
      clientId: 'app_worker_client',
      payload: {
        ops: [operation],
        allowQueryTabEdit: true,
        returnAcceptedOperations: true,
      },
    })
    const deltaResponse = await this.requestSpreadsheetWorkerResponse<SpreadsheetDeltaResponsePayload>(
      command.command,
      command,
      'sheet.delta',
      60000,
      isSpreadsheetDeltaResponsePayload,
    )
    const acceptedOperation = deltaResponse.accepted_operations?.find(
      (accepted) => accepted.op_id === operation.op_id && accepted.op_type === opType && accepted.sheet_id === sheetId,
    )
    if (!acceptedOperation) {
      throw new Error(`Spreadsheet worker did not confirm ${opType} operation`)
    }
    return acceptedOperation
  }

  private spreadsheetSheetNumberFromEngineSheetId(sheetId: unknown): number | null {
    const match = /^sheet_([1-9]\d*)$/.exec(String(sheetId ?? '').trim())
    if (!match) return null
    const id = Number(match[1])
    return Number.isFinite(id) && id > 0 ? Math.floor(id) : null
  }

  async set_range_input_with_operation(
    sheetIdNumber: number,
    startCol: number,
    startRow: number,
    rawInputs: string[][],
  ): Promise<SpreadsheetEngineOperationEnvelope> {
    const rows = Array.isArray(rawInputs) ? rawInputs.map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : [])) : []
    const rowCount = rows.length
    const colCount = rows.reduce((max, row) => Math.max(max, row.length), 0)
    if (rowCount <= 0 || colCount <= 0) {
      throw new Error('set_range_input_with_operation requires a non-empty matrix')
    }
    const normalizedRows = rows.map((row) => (row.length === colCount ? row : [...row, ...new Array(colCount - row.length).fill('')]))
    const requestId = Math.random().toString(36).substring(7)
    const sheetId = buildSpreadsheetEngineSheetId(Math.max(1, Math.floor(Number(sheetIdNumber) || 1)))
    const operation = buildSpreadsheetOperationEnvelope({
      op_id: `app_worker_client_SetRangeInput_${Date.now()}_${requestId}`,
      op_type: 'SetRangeInput',
      workbook_id: 'active_workbook',
      sheet_id: sheetId,
      base_revision: 0,
      actor_client_id: 'app_worker_client',
      created_at_ms: Date.now(),
      payload: {
        range: {
          sheet_id: sheetId,
          start_row: startRow,
          start_col: startCol,
          end_row: startRow + rowCount - 1,
          end_col: startCol + colCount - 1,
        },
        raw_inputs: normalizedRows,
      },
    })
    const command = buildSpreadsheetWorkerCommand({
      command: 'sheet.applyOps',
      requestId,
      workbookId: 'active_workbook',
      sheetId,
      baseRevision: 0,
      clientId: 'app_worker_client',
      payload: {
        ops: [operation],
        allowQueryTabEdit: true,
        returnAcceptedOperations: true,
      },
    })
    const deltaResponse = await this.requestSpreadsheetWorkerResponse<SpreadsheetDeltaResponsePayload>(
      command.command,
      command,
      'sheet.delta',
      60000,
      isSpreadsheetDeltaResponsePayload,
    )
    const acceptedOperation = deltaResponse.accepted_operations?.find(
      (accepted) => accepted.op_id === operation.op_id && accepted.op_type === 'SetRangeInput' && accepted.sheet_id === sheetId,
    )
    if (!acceptedOperation) {
      throw new Error('Spreadsheet worker did not confirm SetRangeInput operation')
    }
    return acceptedOperation
  }

  private nextSpreadsheetSheetNumber(sheets: Array<{ id: number }>): number {
    const used = new Set(sheets.map((sheet) => Math.floor(Number(sheet?.id))).filter((id) => Number.isFinite(id) && id > 0))
    let candidate = 1
    while (used.has(candidate)) candidate += 1
    return candidate
  }

  setExecutionScopeId(executionScopeId: string | null | undefined): void {
    const next = String(executionScopeId ?? '').trim()
    this.defaultExecutionScopeId = next || null
  }

  private withDefaultExecutionScope<T extends Record<string, any> | null | undefined>(payload: T): T | Record<string, any> {
    const skipDefaultScope =
      Boolean((payload as any)?.skipDefaultExecutionScope === true) ||
      Boolean((payload as any)?.skip_default_execution_scope === true) ||
      Boolean((payload as any)?.forceDurableDatasourceNode === true) ||
      Boolean((payload as any)?.force_durable_datasource_node === true) ||
      Boolean((payload as any)?.forceDurableInteractionCube === true) ||
      Boolean((payload as any)?.force_durable_interaction_cube === true) ||
      Boolean((payload as any)?.workbookArtifactWarmup === true) ||
      Boolean((payload as any)?.workbook_artifact_warmup === true)
    if (skipDefaultScope) return (payload ?? null) as T
    const scopeId = String(this.defaultExecutionScopeId ?? '').trim()
    if (!scopeId) return (payload ?? null) as T
    const next = payload && typeof payload === 'object' ? { ...(payload as any) } : {}
    if (!String((next as any).executionScopeId ?? (next as any).execution_scope_id ?? '').trim()) {
      ;(next as any).executionScopeId = scopeId
    }
    return next
  }

  private async withDefaultExecutionScopeLease<T extends Record<string, any> | null | undefined>(
    datasetId: string | null | undefined,
    payload: T,
    requestPriority?: string | null,
  ): Promise<T | Record<string, any>> {
    const next: any = this.withDefaultExecutionScope(payload) ?? {}
    if (requestPriority && !String(next.requestPriority ?? next.request_priority ?? '').trim()) {
      next.requestPriority = requestPriority
    }
    const skipDefaultScope =
      Boolean(next.skipDefaultExecutionScope === true) ||
      Boolean(next.skip_default_execution_scope === true) ||
      Boolean(next.forceDurableDatasourceNode === true) ||
      Boolean(next.force_durable_datasource_node === true) ||
      Boolean(next.forceDurableInteractionCube === true) ||
      Boolean(next.force_durable_interaction_cube === true) ||
      Boolean(next.workbookArtifactWarmup === true) ||
      Boolean(next.workbook_artifact_warmup === true)
    if (skipDefaultScope) return next
    const scopeId = String(next.executionScopeId ?? next.execution_scope_id ?? '').trim()
    const datasetIdNorm = String(datasetId ?? '').trim()
    if (!scopeId || !datasetIdNorm || !scopeId.startsWith('tab:')) return next
    const lease = await ensureTabExecutionLeaseForScope({
      datasetId: datasetIdNorm,
      executionScopeId: scopeId,
    })
    if (lease.executionScopeId && lease.executionScopeId !== scopeId) {
      next.executionScopeId = lease.executionScopeId
      this.defaultExecutionScopeId = lease.executionScopeId
    }
    if (lease.opfsPath) next.tabExecutionOpfsPath = lease.opfsPath
    if (lease.browserTabId) next.browserTabId = lease.browserTabId
    try {
      console.log(
        '[FG-TAB-EXECUTION]',
        JSON.stringify({
          event: lease.repaired ? 'lease_repaired' : 'lease_claimed',
          ts: Date.now(),
          datasetId: datasetIdNorm,
          browserTabId: lease.browserTabId,
          tabExecutionScopeId: lease.executionScopeId || scopeId,
          opfsPath: lease.opfsPath,
          requestPriority: requestPriority || null,
        }),
      )
    } catch {
      // debug logging only
    }
    return next
  }

  private async withRequiredCfoCoreTabExecutionScope<T extends Record<string, any> | null | undefined>(
    payload: T,
    requestPriority?: string | null,
  ): Promise<Record<string, any>> {
    const next: any = this.withDefaultExecutionScope(payload) ?? {}
    if (requestPriority && !String(next.requestPriority ?? next.request_priority ?? '').trim()) {
      next.requestPriority = requestPriority
    }
    const scopeId = String(next.executionScopeId ?? next.execution_scope_id ?? '').trim()
    if (!scopeId || !scopeId.startsWith('tab:')) {
      throw new Error('QuickBooks CFO Core DuckDB requires a tab-scoped executionScopeId')
    }
    const datasetId =
      String(next.persistentDatasetId ?? next.persistent_dataset_id ?? CFO_CORE_DUCKDB_DATASET_ID).trim() || CFO_CORE_DUCKDB_DATASET_ID
    const lease = await ensureTabExecutionLeaseForScope({
      datasetId,
      executionScopeId: scopeId,
    })
    const leasedScopeId = String(lease.executionScopeId || scopeId).trim()
    if (!leasedScopeId || !leasedScopeId.startsWith('tab:') || !lease.opfsPath) {
      throw new Error('QuickBooks CFO Core DuckDB could not claim a tab execution OPFS path')
    }
    next.executionScopeId = leasedScopeId
    next.tabExecutionOpfsPath = lease.opfsPath
    if (lease.browserTabId) next.browserTabId = lease.browserTabId
    next.persistentAdHoc = true
    next.persistentDatasetId = datasetId
    if (lease.executionScopeId && lease.executionScopeId !== scopeId) {
      this.defaultExecutionScopeId = lease.executionScopeId
    }
    try {
      console.log(
        '[CFO-PERF]',
        JSON.stringify({
          event: lease.repaired ? 'cfo_execution_scope_repaired' : 'cfo_execution_scope_ready',
          ts: Date.now(),
          dataset_id: datasetId,
          browser_tab_id: lease.browserTabId || null,
          execution_scope_id: leasedScopeId,
          tab_execution_opfs_path: lease.opfsPath,
        }),
      )
    } catch {
      // debug logging only
    }
    return next
  }

  private async withWorkbookArtifactLock<T>(
    datasetId: string | null | undefined,
    payload: Record<string, any> | null | undefined,
    fn: () => Promise<T>,
  ): Promise<T> {
    const datasetIdNorm = String(datasetId ?? '').trim()
    const needsWorkbookArtifactLock =
      Boolean(payload?.skipDefaultExecutionScope === true) ||
      Boolean(payload?.skip_default_execution_scope === true) ||
      Boolean(payload?.workbookArtifactWarmup === true) ||
      Boolean(payload?.workbook_artifact_warmup === true)
    if (!datasetIdNorm || !needsWorkbookArtifactLock || typeof navigator === 'undefined') {
      return await fn()
    }
    const locks = (navigator as any)?.locks
    if (!locks || typeof locks.request !== 'function') return await fn()
    return await locks.request(`workbook-artifact:${datasetIdNorm}`, { mode: 'exclusive' }, fn)
  }

  private getElectronInvoke(): ((cmd: string, args?: any) => Promise<any>) | null {
    if (typeof window === 'undefined') return null
    const w = window as any
    return w && typeof w.__FG_ELECTRON_INVOKE__ === 'function'
      ? (w.__FG_ELECTRON_INVOKE__ as (cmd: string, args?: any) => Promise<any>)
      : null
  }

  private isInvalidSessionError(err: any): boolean {
    const msg = String(err?.message ?? err ?? '').toLowerCase()
    return msg.includes('invalid session id')
  }

  private async getOrOpenDirectNativeSession(datasetId: string): Promise<string> {
    const id = String(datasetId || '').trim()
    if (!id) throw new Error('cache_run_sql direct-native: missing datasetId')
    const existing = AppWorkerClient.directNativeSessionByDataset.get(id)
    if (existing) return existing
    const invoke = this.getElectronInvoke()
    if (!invoke) throw new Error('cache_run_sql direct-native: Electron invoke bridge unavailable')
    const opened = await invoke('duckdb_open_cache_session', {
      req: { dataset_id: id, require_write: false },
    })
    const sessionId = String((opened as any)?.session_id || '').trim()
    if (!sessionId) throw new Error('cache_run_sql direct-native: missing session_id')
    AppWorkerClient.directNativeSessionByDataset.set(id, sessionId)
    return sessionId
  }

  private async runDirectNativeArrowQuery(
    datasetId: string,
    sql: string,
    bindParams: Array<{
      type: 'null' | 'boolean' | 'number' | 'string'
      value?: unknown
    }>,
  ): Promise<any> {
    const invoke = this.getElectronInvoke()
    if (!invoke) throw new Error('cache_run_sql direct-native: Electron invoke bridge unavailable')
    let directSessionMs: number | null = null
    let directInvokeWallMs: number | null = null
    let directInvokeWallPerfMs: number | null = null
    let directSessionReused: boolean | null = null
    let invokeAwaitResumedEpochMs: number | null = null
    let invokeAwaitResumedPerfMs: number | null = null
    let invokeStartedAtEpochMs: number | null = null
    let directInvokeTimerProbeTickCount: number | null = null
    let directInvokeTimerProbeMaxDriftMs: number | null = null
    let directInvokeTimerProbeFirstTickMs: number | null = null
    let directInvokeTimerProbePendingAtResolve: boolean | null = null
    const nowPerf = (): number =>
      typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now()

    const runOnce = async (): Promise<any> => {
      const sessionLookupStartedAt = Date.now()
      const hadSession = AppWorkerClient.directNativeSessionByDataset.has(String(datasetId || '').trim())
      const sessionId = await this.getOrOpenDirectNativeSession(datasetId)
      directSessionMs = Math.max(0, Date.now() - sessionLookupStartedAt)
      directSessionReused = hadSession
      const invokeStartedAt = Date.now()
      invokeStartedAtEpochMs = invokeStartedAt
      const invokeStartedPerf = nowPerf()
      const timerProbeIntervalMs = 50
      let timerProbeTickCount = 0
      let timerProbeMaxDriftMs = 0
      let timerProbeFirstTickPerfMs: number | null = null
      let timerProbeExpectedPerfMs = invokeStartedPerf + timerProbeIntervalMs
      const timerProbeId = setInterval(() => {
        const now = nowPerf()
        timerProbeTickCount += 1
        if (timerProbeFirstTickPerfMs === null) {
          timerProbeFirstTickPerfMs = now
        }
        const driftMs = Math.max(0, now - timerProbeExpectedPerfMs)
        if (driftMs > timerProbeMaxDriftMs) timerProbeMaxDriftMs = driftMs
        timerProbeExpectedPerfMs = now + timerProbeIntervalMs
      }, timerProbeIntervalMs)
      let out: any
      try {
        out = await invoke('duckdb_query_arrow_ipc', {
          __fg_renderer_sent_at_epoch_ms: invokeStartedAt,
          req: {
            session_id: sessionId,
            sql,
            emit_perf: true,
            params: bindParams,
          },
        })
      } finally {
        clearInterval(timerProbeId)
        directInvokeTimerProbeTickCount = timerProbeTickCount
        directInvokeTimerProbeMaxDriftMs = timerProbeMaxDriftMs
        directInvokeTimerProbeFirstTickMs =
          timerProbeFirstTickPerfMs !== null && Number.isFinite(timerProbeFirstTickPerfMs)
            ? Math.max(0, Number(timerProbeFirstTickPerfMs) - (invokeStartedPerf + timerProbeIntervalMs))
            : null
        directInvokeTimerProbePendingAtResolve = timerProbeTickCount <= 0
      }
      invokeAwaitResumedEpochMs = Date.now()
      invokeAwaitResumedPerfMs = nowPerf()
      directInvokeWallMs = Math.max(0, Date.now() - invokeStartedAt)
      directInvokeWallPerfMs = Math.max(0, nowPerf() - invokeStartedPerf)
      return out
    }

    let out: any
    try {
      out = await runOnce()
    } catch (e: any) {
      if (!this.isInvalidSessionError(e)) throw e
      AppWorkerClient.directNativeSessionByDataset.delete(String(datasetId || '').trim())
      out = await runOnce()
    }

    const bytesRaw = out && typeof out === 'object' ? (out as any).bytes : out
    const ipcBuffer = bytesRaw instanceof Uint8Array ? bytesRaw : bytesRaw instanceof ArrayBuffer ? new Uint8Array(bytesRaw) : null
    if (!(ipcBuffer instanceof Uint8Array) || ipcBuffer.byteLength <= 0) {
      throw new Error('cache_run_sql direct-native: invalid Arrow IPC payload')
    }

    const nativePerf =
      out && typeof out === 'object' && (out as any).perf && typeof (out as any).perf === 'object' ? (out as any).perf : null
    const invokePerf =
      out && typeof out === 'object' && (out as any).__fg_invoke_perf && typeof (out as any).__fg_invoke_perf === 'object'
        ? (out as any).__fg_invoke_perf
        : out && typeof out === 'object' && (out as any).bridge_perf && typeof (out as any).bridge_perf === 'object'
          ? (out as any).bridge_perf
          : null
    const responseReceivedEpochMs =
      Number.isFinite(Number(invokeAwaitResumedEpochMs)) && Number(invokeAwaitResumedEpochMs) > 0
        ? Number(invokeAwaitResumedEpochMs)
        : Date.now()
    const responseReceivedPerfMs = Number.isFinite(Number(invokeAwaitResumedPerfMs)) ? Number(invokeAwaitResumedPerfMs) : nowPerf()
    const bridgeTotalMsForEpoch = Number((invokePerf as any)?.bridge_total_ms ?? NaN)
    const bridgeReturnEpochMs = Number((invokePerf as any)?.bridge_return_epoch_ms ?? NaN)
    const inferredBridgeReturnEpochMs =
      Number.isFinite(bridgeReturnEpochMs) && bridgeReturnEpochMs > 0
        ? bridgeReturnEpochMs
        : Number.isFinite(bridgeTotalMsForEpoch)
          ? (Number(invokeStartedAtEpochMs) || responseReceivedEpochMs) + Math.max(0, Number(bridgeTotalMsForEpoch))
          : NaN
    const invokeResumeLagMs =
      Number.isFinite(inferredBridgeReturnEpochMs) && inferredBridgeReturnEpochMs > 0
        ? Math.max(0, responseReceivedEpochMs - inferredBridgeReturnEpochMs)
        : null
    const bridgeTotalMs = bridgeTotalMsForEpoch
    const invokeResumeAttributionGapMs =
      Number.isFinite(Number(invokeResumeLagMs)) && Number.isFinite(bridgeTotalMs)
        ? Math.max(0, Number(invokeResumeLagMs) - Math.max(0, bridgeTotalMs))
        : null
    const postResumeParseStartedPerfMs = nowPerf()
    let rowCount = Number((nativePerf as any)?.row_count ?? NaN)
    if (!Number.isFinite(rowCount)) {
      try {
        const table = tableFromIPC(ipcBuffer)
        rowCount = Number(table?.numRows ?? 0)
      } catch {
        rowCount = 0
      }
    }
    const postResumeParseMs = Math.max(0, nowPerf() - postResumeParseStartedPerfMs)
    const responsePackedEpochMs = Date.now()
    const responsePackedPerfMs = nowPerf()
    const localPerf = {
      app_call_queue_wait_ms: 0,
      direct_session_ms: Number.isFinite(Number(directSessionMs)) ? Number(directSessionMs) : null,
      direct_session_reused: directSessionReused === true,
      direct_invoke_wall_ms: Number.isFinite(Number(directInvokeWallMs)) ? Number(directInvokeWallMs) : null,
      direct_invoke_wall_perf_ms: Number.isFinite(Number(directInvokeWallPerfMs)) ? Number(directInvokeWallPerfMs) : null,
      direct_invoke_timer_probe_tick_count: Number.isFinite(Number(directInvokeTimerProbeTickCount))
        ? Number(directInvokeTimerProbeTickCount)
        : null,
      direct_invoke_timer_probe_max_drift_ms: Number.isFinite(Number(directInvokeTimerProbeMaxDriftMs))
        ? Number(directInvokeTimerProbeMaxDriftMs)
        : null,
      direct_invoke_timer_probe_first_tick_ms:
        Number.isFinite(Number(directInvokeTimerProbeFirstTickMs)) && directInvokeTimerProbeFirstTickMs !== null
          ? Number(directInvokeTimerProbeFirstTickMs)
          : null,
      direct_invoke_timer_probe_pending_at_resolve: directInvokeTimerProbePendingAtResolve === true,
      local_exec_ms: Number.isFinite(Number((nativePerf as any)?.total_native_ms)) ? Number((nativePerf as any).total_native_ms) : null,
      native_invoke_ms_total: Number.isFinite(Number((invokePerf as any)?.bridge_total_ms))
        ? Number((invokePerf as any).bridge_total_ms)
        : null,
      native_bridge_total_ms: Number.isFinite(Number((invokePerf as any)?.bridge_total_ms))
        ? Number((invokePerf as any).bridge_total_ms)
        : null,
      native_bridge_fetch_ms_total: Number.isFinite(Number((invokePerf as any)?.bridge_fetch_ms_total))
        ? Number((invokePerf as any).bridge_fetch_ms_total)
        : null,
      native_bridge_parse_ms: Number.isFinite(Number((invokePerf as any)?.bridge_parse_ms))
        ? Number((invokePerf as any).bridge_parse_ms)
        : null,
      native_bridge_main_queue_wait_ms: Number.isFinite(Number((invokePerf as any)?.bridge_main_queue_wait_ms))
        ? Number((invokePerf as any).bridge_main_queue_wait_ms)
        : null,
      native_bridge_preload_to_main_queue_wait_ms: Number.isFinite(Number((invokePerf as any)?.bridge_preload_to_main_queue_wait_ms))
        ? Number((invokePerf as any).bridge_preload_to_main_queue_wait_ms)
        : null,
      native_bridge_encode_args_ms: Number.isFinite(Number((invokePerf as any)?.bridge_encode_args_ms))
        ? Number((invokePerf as any).bridge_encode_args_ms)
        : null,
      native_bridge_return_assemble_ms: Number.isFinite(Number((invokePerf as any)?.bridge_return_assemble_ms))
        ? Number((invokePerf as any).bridge_return_assemble_ms)
        : null,
      native_bridge_total_perf_ms: Number.isFinite(Number((invokePerf as any)?.bridge_total_perf_ms))
        ? Number((invokePerf as any).bridge_total_perf_ms)
        : null,
      native_preload_after_main_return_ms: Number.isFinite(Number((invokePerf as any)?.preload_after_main_return_ms))
        ? Number((invokePerf as any).preload_after_main_return_ms)
        : null,
      native_preload_invoke_roundtrip_ms: Number.isFinite(Number((invokePerf as any)?.preload_invoke_roundtrip_ms))
        ? Number((invokePerf as any).preload_invoke_roundtrip_ms)
        : null,
      native_preload_timer_probe_tick_count: Number.isFinite(Number((invokePerf as any)?.preload_timer_probe_tick_count))
        ? Number((invokePerf as any).preload_timer_probe_tick_count)
        : null,
      native_preload_timer_probe_max_drift_ms: Number.isFinite(Number((invokePerf as any)?.preload_timer_probe_max_drift_ms))
        ? Number((invokePerf as any).preload_timer_probe_max_drift_ms)
        : null,
      native_preload_timer_probe_first_tick_ms:
        Number.isFinite(Number((invokePerf as any)?.preload_timer_probe_first_tick_ms)) &&
        (invokePerf as any)?.preload_timer_probe_first_tick_ms !== null
          ? Number((invokePerf as any).preload_timer_probe_first_tick_ms)
          : null,
      native_preload_timer_probe_pending_at_resolve: (invokePerf as any)?.preload_timer_probe_pending_at_resolve === true,
      native_preload_sent_at_epoch_ms: Number.isFinite(Number((invokePerf as any)?.preload_sent_at_epoch_ms))
        ? Number((invokePerf as any).preload_sent_at_epoch_ms)
        : null,
      native_bridge_invoke_resume_lag_ms: Number.isFinite(Number(invokeResumeLagMs)) ? Number(invokeResumeLagMs) : null,
      native_bridge_invoke_resume_attribution_gap_ms: Number.isFinite(Number(invokeResumeAttributionGapMs))
        ? Number(invokeResumeAttributionGapMs)
        : null,
      native_bridge_attempt_count: Number.isFinite(Number((invokePerf as any)?.bridge_attempt_count))
        ? Number((invokePerf as any).bridge_attempt_count)
        : null,
      app_response_received_epoch_ms: responseReceivedEpochMs,
      app_response_sent_epoch_ms:
        Number.isFinite(inferredBridgeReturnEpochMs) && inferredBridgeReturnEpochMs > 0
          ? Number(inferredBridgeReturnEpochMs)
          : responseReceivedEpochMs,
      response_delivery_lag_ms: Number.isFinite(Number(invokeResumeLagMs)) ? Number(invokeResumeLagMs) : null,
      direct_post_resume_parse_ms: Number.isFinite(Number(postResumeParseMs)) ? Number(postResumeParseMs) : null,
      direct_post_resume_total_ms: Math.max(0, responsePackedPerfMs - responseReceivedPerfMs),
      direct_response_packed_epoch_ms: responsePackedEpochMs,
      renderer_direct_native: true,
    }

    return {
      dataset_id: datasetId,
      route: 'local',
      rows: Math.max(0, Math.floor(rowCount)),
      ipc_buffer: ipcBuffer,
      preview_rows: [],
      native_perf: nativePerf,
      local_perf: localPerf,
    }
  }

  // Wrapper for generic calls if needed
  async appCall(method: string, args: any = {}): Promise<any> {
    return this.bus.request(method, args)
  }

  // --- Tab Management ---

  async list_tabs(): Promise<
    {
      id: string
      name: string
      kind: 'sheet' | 'query' | 'datasource' | 'dashboard'
    }[]
  > {
    return this.bus.request('list_tabs', [])
  }

  async get_active_tab_id(): Promise<string> {
    return this.bus.request('get_active_tab_id', [])
  }

  async create_spreadsheet_tab(name: string): Promise<any> {
    return this.bus.request('create_spreadsheet_tab', [name])
  }

  async create_query_tab(name: string): Promise<any> {
    return this.bus.request('create_query_tab', [name])
  }

  async create_datasource_tab(name: string): Promise<any> {
    return this.bus.request('create_datasource_tab', [name])
  }

  async create_dashboard_tab(name: string): Promise<any> {
    return this.bus.request('create_dashboard_tab', [name])
  }

  async rename_tab(id: string, name: string): Promise<void> {
    return this.bus.request('rename_tab', [id, name])
  }

  async activate_tab(id: string): Promise<void> {
    return this.bus.request('activate_tab', [id])
  }

  // --- Query Builder State ---

  async get_query_builder_state(tabId: string): Promise<any> {
    return this.bus.request('get_query_builder_state', [tabId])
  }

  async save_query_builder_state(tabId: string, state: any): Promise<void> {
    return this.bus.request('save_query_builder_state', [tabId, state])
  }

  async get_query_visual_spec(tabId: string): Promise<any> {
    return this.bus.request('get_query_visual_spec', [tabId])
  }

  async save_query_visual_spec(tabId: string, spec: any): Promise<void> {
    return this.bus.request('save_query_visual_spec', [tabId, spec])
  }

  async preview_query_sql(tabId: string): Promise<string> {
    return this.bus.request('preview_query_sql', [tabId])
  }

  async run_query_tab(tabId: string, opts?: any): Promise<any> {
    // Query execution can legitimately take >30s when it routes to MotherDuck (network + remote execution),
    // so use a longer timeout to avoid the UI timing out while the worker is still working.
    return this.bus.request('run_query_tab', [tabId, opts ?? null], {
      timeoutMs: 180000,
    })
  }

  /**
   * Cache externally-executed query results into the worker so Viz (RenderPlan builder)
   * can render from `{ kind: 'query_tab', tabId }` sources.
   *
   * This is used when the UI runs a query server-side (e.g. external DB server-direct)
   * and the worker never sees `run_query_tab`.
   */
  async cache_query_tab_result(tabId: string, result: any): Promise<any> {
    return this.bus.request('cache_query_tab_result', [tabId, result], {
      timeoutMs: 120000,
    })
  }

  /**
   * Get query result data for a tab (for dashboard worksheets).
   * Returns array of row objects suitable for CrosstabView.
   */
  async get_query_tab_result(tabId: string): Promise<{
    data: any[]
    headers: string[]
    rows: number
    cols: number
    plannerMetadata?: any | null
  } | null> {
    try {
      const result = await this.bus.request('get_query_tab_result', [tabId], {
        timeoutMs: 10000,
      })
      if (result && result.success) {
        return {
          data: result.data || [],
          headers: result.headers || [],
          rows: result.rows || 0,
          cols: result.cols || 0,
          plannerMetadata: result.plannerMetadata ?? null,
        }
      }
      return null
    } catch (err) {
      console.warn('[AppWorkerClient] Failed to get query tab result:', err)
      return null
    }
  }

  // --- Visualization (RenderPlan builder) ---
  async viz_build_render_plan(args: any): Promise<any> {
    return this.bus.request('viz_build_render_plan', [args], {
      timeoutMs: 60000,
    })
  }

  // --- Viz Raster ABI (scaffold) ---
  async viz_register_raster_surface(args: { surfaceId: string; buffer: SharedArrayBuffer }): Promise<any> {
    return this.bus.request('viz_register_raster_surface', [args], {
      timeoutMs: 30000,
    })
  }

  async viz_raster_test_pattern(args: { surfaceId: string }): Promise<any> {
    return this.bus.request('viz_raster_test_pattern', [args], {
      timeoutMs: 30000,
    })
  }

  async viz_rasterize_plan(args: { surfaceId: string; plan: any }): Promise<any> {
    return this.bus.request('viz_rasterize_plan', [args], { timeoutMs: 60000 })
  }

  async viz_create_wasm_raster_surface(args: { widthCss: number; heightCss: number; dpr: number }): Promise<any> {
    return this.bus.request('viz_create_wasm_raster_surface', [args], {
      timeoutMs: 30000,
    })
  }

  // --- Tableau Runtime (WASM in worker) ---

  async tableau_format_batch(
    items: Array<{ key: string; value: any; format?: string | null }>,
  ): Promise<Array<{ key: string; formatted: string }>> {
    return this.bus.request('tableau_format_batch', [items], {
      timeoutMs: 30000,
    })
  }

  /**
   * Build worksheet SQL (and optionally an execution plan) in the worker.
   *
   * Enterprise-grade UX requirement: query compilation can be CPU heavy; doing it on the UI thread
   * causes freezes during interactive authoring (e.g. removing a shelf field). Offloading to the
   * worker keeps the UI responsive and lets us add cancellation/coalescing.
   */
  async tableau_build_worksheet_query(args: {
    worksheetSpec: any
    options: any
    emitExecPlan?: boolean
    emitAggBasePlan?: boolean
    emitPrecompute?: boolean
    datasetId?: string
    dataset_id?: string
    executionScopeId?: string
    execution_scope_id?: string
    // Optional: caller-provided cache key (avoids expensive worker-side stringify of large options)
    cacheKey?: string
    // Optional: worker-stored builder context key (workbookIr/bindingMap live in worker memory)
    contextKey?: string
    // Optional: return a cache miss instead of doing a fresh compile. Used by background warmups.
    skipFreshBuild?: boolean
    skip_fresh_build?: boolean
  }): Promise<{
    kind: 'sql' | 'plan'
    sql: string
    plan?: any
    buildMs?: number
  }> {
    // Query compilation can take a while on large sheets; allow a longer timeout.
    const datasetId = String(args?.datasetId ?? args?.dataset_id ?? args?.options?.datasetId ?? args?.options?.dataset_id ?? '').trim()
    const scopedArgs = await this.withDefaultExecutionScopeLease(datasetId || null, args as any, 'worksheet_build')
    const coalesceKey = (() => {
      const cacheKey = String((scopedArgs as any)?.cacheKey ?? '').trim()
      if (!cacheKey) return ''
      if (Boolean((scopedArgs as any)?.skipFreshBuild ?? (scopedArgs as any)?.skip_fresh_build)) return ''
      return JSON.stringify({
        cacheKey,
        datasetId:
          String(
            (scopedArgs as any)?.datasetId ??
              (scopedArgs as any)?.dataset_id ??
              (scopedArgs as any)?.options?.datasetId ??
              (scopedArgs as any)?.options?.dataset_id ??
              '',
          ).trim() || null,
        executionScopeId: String((scopedArgs as any)?.executionScopeId ?? (scopedArgs as any)?.execution_scope_id ?? '').trim() || null,
        contextKey: String((scopedArgs as any)?.contextKey ?? '').trim() || null,
        emitExecPlan: Boolean((scopedArgs as any)?.emitExecPlan),
        emitAggBasePlan: Boolean((scopedArgs as any)?.emitAggBasePlan),
        emitPrecompute: Boolean((scopedArgs as any)?.emitPrecompute),
        skipPersistentCacheReads: Boolean(
          (scopedArgs as any)?.skipPersistentCacheReads ?? (scopedArgs as any)?.skip_persistent_cache_reads,
        ),
      })
    })()
    const existingBuild = coalesceKey ? this.tableauWorksheetBuildInFlight.get(coalesceKey) : null
    let res = await (existingBuild ||
      (() => {
        const request = this.bus.request('tableau_build_worksheet_query', [scopedArgs], { timeoutMs: 180000 })
        if (coalesceKey) {
          this.tableauWorksheetBuildInFlight.set(coalesceKey, request)
          const cleanup = () => {
            if (this.tableauWorksheetBuildInFlight.get(coalesceKey) === request) {
              this.tableauWorksheetBuildInFlight.delete(coalesceKey)
            }
          }
          request.then(cleanup, cleanup)
        }
        return request
      })())
    if (existingBuild && res && typeof res === 'object') {
      res = {
        ...(res as any),
        cache: (res as any).cache === 'hit' ? 'hit' : 'coalesced',
        cacheStore: String((res as any).cacheStore ?? '').trim() || 'client-inflight',
        clientCoalesced: true,
      }
    }
    if (shouldDumpSqlToLogs()) {
      try {
        const kind = String((res as any)?.kind ?? '')
        const sql = String((res as any)?.sql ?? '')
        const plan = (res as any)?.plan ?? null
        const worksheetName = String(args?.options?.worksheetName ?? args?.options?.worksheet_name ?? '').trim() || null
        const meta = {
          method: 'tableau_build_worksheet_query',
          kind,
          worksheetName,
          emitExecPlan: Boolean(args?.emitExecPlan),
          emitAggBasePlan: Boolean((args as any)?.emitAggBasePlan),
          emitPrecompute: Boolean(args?.emitPrecompute),
          sqlLength: sql.length,
          hasPlan: Boolean(plan && typeof plan === 'object'),
        }
        if (plan && typeof plan === 'object') {
          const stmts: string[] = Array.isArray((plan as any).statements) ? (plan as any).statements : []
          const cleanup: string[] = Array.isArray((plan as any).cleanup) ? (plan as any).cleanup : []
          const finalSql = String((plan as any).finalSql ?? sql ?? '')
          const body = [
            `-- statements (${stmts.length})`,
            ...stmts.map((s: any, i: number) => `-- statement[${i}]\n${String(s ?? '')}`),
            `-- finalSql`,
            finalSql,
            cleanup.length ? `-- cleanup (${cleanup.length})` : '',
            ...cleanup.map((s: any, i: number) => `-- cleanup[${i}]\n${String(s ?? '')}`),
          ]
            .filter((x) => String(x ?? '').trim().length > 0)
            .join('\n\n')
          logSqlDumpBlock('tableau_build_worksheet_query(plan)', body, meta)
        } else {
          logSqlDumpBlock('tableau_build_worksheet_query(sql)', sql, meta)
        }
      } catch {}
    }
    return res
  }

  /**
   * Store large, mostly-stable query builder context objects inside the worker (desktop perf).
   * This avoids repeatedly structured-cloning `workbookIr` + `bindingMap` for every interaction.
   */
  async tableau_set_builder_context(args: { contextKey: string; workbookIr?: any | null; bindingMap?: any | null }): Promise<{
    ok: boolean
    contextKey: string
    hasWorkbookIr?: boolean
    hasBindingMap?: boolean
  }> {
    return this.bus.request('tableau_set_builder_context', [args], {
      timeoutMs: 60000,
    })
  }

  async update_query_source_fields(tabId: string, sourceId: string, fields: any[]) {
    return this.bus.request('update_query_source_fields', [tabId, sourceId, fields])
  }

  async get_sheets(): Promise<
    Array<{
      id: number
      name: string
      row_count?: number
      col_count?: number
      row_metadata?: Record<string, Record<string, unknown>>
      column_metadata?: Record<string, Record<string, unknown>>
      frozen_panes?: { rows: number; cols: number } | null
      hidden?: boolean
    }>
  > {
    const response = await this.requestSpreadsheetSheetList()
    const raw = Array.isArray(response?.sheets) ? response.sheets : []
    if (!Array.isArray(raw)) return []
    return raw
      .map((row: any) => {
        const id = Number(row?.id)
        const name = String(row?.name ?? '').trim()
        if (!Number.isFinite(id) || id <= 0 || !name) return null
        const rowCount = Math.floor(Number(row?.row_count ?? row?.rowCount))
        const colCount = Math.floor(Number(row?.col_count ?? row?.colCount))
        const rowMetadata =
          row?.row_metadata && typeof row.row_metadata === 'object'
            ? (row.row_metadata as Record<string, Record<string, unknown>>)
            : undefined
        const columnMetadata =
          row?.column_metadata && typeof row.column_metadata === 'object'
            ? (row.column_metadata as Record<string, Record<string, unknown>>)
            : undefined
        const frozenPanes =
          row?.frozen_panes && typeof row.frozen_panes === 'object'
            ? {
                rows: Math.max(0, Math.floor(Number(row.frozen_panes.rows) || 0)),
                cols: Math.max(0, Math.floor(Number(row.frozen_panes.cols) || 0)),
              }
            : undefined
        return {
          id: Math.floor(id),
          name,
          ...(Number.isFinite(rowCount) && rowCount > 0 ? { row_count: rowCount } : null),
          ...(Number.isFinite(colCount) && colCount > 0 ? { col_count: colCount } : null),
          ...(rowMetadata ? { row_metadata: rowMetadata } : null),
          ...(columnMetadata ? { column_metadata: columnMetadata } : null),
          ...(frozenPanes ? { frozen_panes: frozenPanes } : null),
          ...(row?.hidden === true ? { hidden: true } : null),
        }
      })
      .filter(Boolean) as Array<{
      id: number
      name: string
      row_count?: number
      col_count?: number
      row_metadata?: Record<string, Record<string, unknown>>
      column_metadata?: Record<string, Record<string, unknown>>
      frozen_panes?: { rows: number; cols: number } | null
      hidden?: boolean
    }>
  }

  async add_sheet(name: string): Promise<number> {
    const out = await this.add_sheet_with_operation(name)
    return out.id
  }

  async add_sheet_with_operation(
    name: string,
    opts?: { row_count?: number | null; col_count?: number | null },
  ): Promise<{
    id: number
    operation: SpreadsheetEngineOperationEnvelope | null
  }> {
    const nextName = String(name ?? '').trim() || 'Sheet'
    const rowCount = Math.max(1, Math.min(20000, Math.floor(Number(opts?.row_count ?? 200) || 200)))
    const colCount = Math.max(1, Math.min(2000, Math.floor(Number(opts?.col_count ?? 26) || 26)))
    const listResponse = await this.requestSpreadsheetSheetList()
    const id = this.nextSpreadsheetSheetNumber(Array.isArray(listResponse?.sheets) ? listResponse.sheets : [])
    const operation = await this.submitSpreadsheetSheetMetadataOperation('AddSheet', id, {
      name: nextName,
      row_count: rowCount,
      col_count: colCount,
    })
    const acceptedId = this.spreadsheetSheetNumberFromEngineSheetId(operation.sheet_id)
    if (acceptedId == null) {
      throw new Error('Spreadsheet worker returned invalid AddSheet sheet id')
    }
    return {
      id: acceptedId,
      operation,
    }
  }

  async ensure_sheet_with_operation(
    sheetId: number,
    name: string,
  ): Promise<{
    id: number
    operation: SpreadsheetEngineOperationEnvelope | null
  }> {
    const id = Math.max(1, Math.floor(Number(sheetId) || 1))
    const nextName = String(name ?? '').trim() || `Sheet ${id}`
    const listResponse = await this.requestSpreadsheetSheetList()
    const sheets = Array.isArray(listResponse?.sheets) ? listResponse.sheets : []
    if (sheets.some((sheet: any) => Math.floor(Number(sheet?.id)) === id)) {
      return { id, operation: null }
    }
    const operation = await this.submitSpreadsheetSheetMetadataOperation('AddSheet', id, {
      name: nextName,
      row_count: 200,
      col_count: 26,
    })
    const acceptedId = this.spreadsheetSheetNumberFromEngineSheetId(operation.sheet_id)
    if (acceptedId !== id) {
      throw new Error('Spreadsheet worker returned unexpected AddSheet sheet id')
    }
    return {
      id: acceptedId,
      operation,
    }
  }

  async duplicate_sheet(sheetId: number, name?: string, index?: number): Promise<number> {
    const out = await this.duplicate_sheet_with_operation(sheetId, name, index)
    return out.id
  }

  async duplicate_sheet_with_operation(
    sheetId: number,
    name?: string,
    index?: number,
  ): Promise<{
    id: number
    operation: SpreadsheetEngineOperationEnvelope | null
  }> {
    const sourceId = Math.max(1, Math.floor(Number(sheetId) || 1))
    const listResponse = await this.requestSpreadsheetSheetList()
    const sheets = Array.isArray(listResponse?.sheets) ? listResponse.sheets : []
    const sourceSheet = sheets.find((sheet: any) => Math.floor(Number(sheet?.id)) === sourceId)
    const sourceName = String(sourceSheet?.name ?? `Sheet ${sourceId}`).trim() || `Sheet ${sourceId}`
    const nextName = String(name ?? '').trim() || `${sourceName} (2)`
    const targetId = this.nextSpreadsheetSheetNumber(sheets)
    const payload: SpreadsheetSheetMetadataOperationPayload = {
      source_sheet_id: buildSpreadsheetEngineSheetId(sourceId),
      name: nextName,
    }
    if (Number.isFinite(Number(index))) {
      payload.index = Math.max(0, Math.floor(Number(index)))
    }
    const operation = await this.submitSpreadsheetSheetMetadataOperation('DuplicateSheet', targetId, payload)
    const acceptedId = this.spreadsheetSheetNumberFromEngineSheetId(operation.sheet_id)
    if (acceptedId == null) {
      throw new Error('Spreadsheet worker returned invalid DuplicateSheet sheet id')
    }
    return {
      id: acceptedId,
      operation,
    }
  }

  async rename_sheet(sheetId: number, name: string): Promise<void> {
    await this.rename_sheet_with_operation(sheetId, name)
  }

  async rename_sheet_with_operation(sheetId: number, name: string): Promise<SpreadsheetEngineOperationEnvelope | null> {
    const id = Math.max(1, Math.floor(Number(sheetId) || 1))
    const nextName = String(name ?? '').trim()
    return this.submitSpreadsheetSheetMetadataOperation('RenameSheet', id, {
      name: nextName,
    })
  }

  async delete_sheet(sheetId: number): Promise<void> {
    await this.delete_sheet_with_operation(sheetId)
  }

  async delete_sheet_with_operation(sheetId: number): Promise<SpreadsheetEngineOperationEnvelope | null> {
    const id = Math.max(1, Math.floor(Number(sheetId) || 1))
    return this.submitSpreadsheetSheetMetadataOperation('DeleteSheet', id, {})
  }

  async move_sheet(sheetId: number, index: number): Promise<void> {
    await this.move_sheet_with_operation(sheetId, index)
  }

  async move_sheet_with_operation(sheetId: number, index: number): Promise<SpreadsheetEngineOperationEnvelope | null> {
    const id = Math.max(1, Math.floor(Number(sheetId) || 1))
    const nextIndex = Math.max(0, Math.floor(Number(index) || 0))
    return this.submitSpreadsheetSheetMetadataOperation('MoveSheet', id, {
      index: nextIndex,
    })
  }

  async set_sheet_hidden(sheetId: number, hidden: boolean): Promise<void> {
    await this.set_sheet_hidden_with_operation(sheetId, hidden)
  }

  async set_sheet_hidden_with_operation(sheetId: number, hidden: boolean): Promise<SpreadsheetEngineOperationEnvelope | null> {
    const id = Math.max(1, Math.floor(Number(sheetId) || 1))
    return this.submitSpreadsheetSheetMetadataOperation('SetSheetHidden', id, {
      hidden: Boolean(hidden),
    })
  }

  async set_sheet_tab_color(sheetId: number, tabColor: Record<string, unknown> | null): Promise<void> {
    await this.set_sheet_tab_color_with_operation(sheetId, tabColor)
  }

  async set_sheet_tab_color_with_operation(
    sheetId: number,
    tabColor: Record<string, unknown> | null,
  ): Promise<SpreadsheetEngineOperationEnvelope | null> {
    const id = Math.max(1, Math.floor(Number(sheetId) || 1))
    return this.submitSpreadsheetSheetMetadataOperation('SetSheetTabColor', id, { tab_color: tabColor })
  }

  async set_sheet_protection(sheetId: number, protection: Record<string, unknown> | null): Promise<void> {
    await this.set_sheet_protection_with_operation(sheetId, protection)
  }

  async set_sheet_protection_with_operation(
    sheetId: number,
    protection: Record<string, unknown> | null,
  ): Promise<SpreadsheetEngineOperationEnvelope | null> {
    const id = Math.max(1, Math.floor(Number(sheetId) || 1))
    return this.submitSpreadsheetSheetMetadataOperation('SetSheetProtection', id, { protection })
  }

  async set_active_sheet(sheetId: number): Promise<void> {
    void sheetId
  }

  // --- HTTPFS ---

  async register_httpfs_source(id: string | undefined, name: string, url: string, auth_hint: string = 'none'): Promise<void> {
    return this.bus.request('register_httpfs_source', [id, name, url, auth_hint])
  }

  async list_httpfs_sources(): Promise<any[]> {
    return this.bus.request('list_httpfs_sources', [])
  }

  async refresh_httpfs_source(id: string): Promise<void> {
    return this.bus.request('refresh_httpfs_source', [id])
  }

  async fetch_httpfs_range(id: string, start: bigint, end: bigint): Promise<any> {
    // BigInt serialization might need care, but assuming generic handling or string if needed.
    // WASM usually expects numbers or strings. Rust might expect u64.
    // Let's pass as is and hope serde/wasm-bindgen handles it or we cast to Number if safe.
    // start/end are offsets.
    return this.bus.request('fetch_httpfs_range', [id, Number(start), Number(end)])
  }

  async fetch_httpfs_tail(id: string, len: number): Promise<Uint8Array> {
    return this.bus.request('fetch_httpfs_tail', [id, len])
  }

  async read_parquet_footer_size(tail_bytes: Uint8Array): Promise<number | null> {
    return this.bus.request('read_parquet_footer_size', [tail_bytes])
  }

  async parse_parquet_footer_metadata(footer_bytes: Uint8Array): Promise<any> {
    return this.bus.request('parse_parquet_footer_metadata', [footer_bytes])
  }

  async record_httpfs_metadata(id: string, metadata: any, footer_bytes: Uint8Array): Promise<void> {
    return this.bus.request('record_httpfs_metadata', [id, metadata, footer_bytes])
  }

  // --- MotherDuck ---

  async list_md_tables(): Promise<any[]> {
    return []
  }

  async md_connect(_mdToken: string, _database?: string | null, _opts?: any): Promise<any> {
    throw new Error('motherduck_disabled: MotherDuck is not an enabled FuseGrid datasource')
  }

  // --- Cache Test / Local Cache ---

  async cache_get_manifest(datasetId?: string, opts?: { timeoutMs?: number }): Promise<any> {
    // cache_get_manifest can involve IndexedDB reads + JSON parsing and may exceed
    // our default 30s WorkerBus timeout on larger datasets, so allow/extend timeout.
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    return this.bus.request('cache_get_manifest', [datasetId ?? null], {
      timeoutMs,
    })
  }

  async cache_check_prepared_local_cache_state(
    datasetId: string,
    payload?: {
      rawTableName?: string | null
      semanticRelationName?: string | null
    } | null,
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 10000))
    return this.bus.request('cache_check_prepared_local_cache_state', [datasetId ?? null, payload ?? null], { timeoutMs })
  }

  async cache_get_refresh_status(
    datasetId: string,
    payload?: {
      source?: {
        url?: string | null
        extract_uri?: string | null
        extract_version?: string | number | null
        extract_identity?: string | null
        etag?: string | null
      } | null
    } | null,
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    return this.bus.request('cache_get_refresh_status', [datasetId, payload ?? null], { timeoutMs })
  }

  async cache_seed_manifest(datasetId: string, manifest: any, opts?: { timeoutMs?: number }): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    return this.bus.request('cache_seed_manifest', [datasetId ?? null, manifest ?? null], { timeoutMs })
  }

  async cache_exec_sql(datasetId: string, sql: string, opts?: { timeoutMs?: number; route?: 'local' | 'remote' | 'auto' }): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    const workerOpts = await this.withDefaultExecutionScopeLease(datasetId, opts ?? null, 'foreground_render')
    return this.bus.request('cache_exec_sql', [datasetId ?? null, sql ?? null, workerOpts ?? null], { timeoutMs })
  }

  async cache_query_sql(
    datasetId: string,
    sql: string,
    opts?: {
      timeoutMs?: number
      previewRowLimit?: number
      preview_row_limit?: number
      route?: 'local' | 'remote' | 'auto'
    },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    const workerOpts = await this.withDefaultExecutionScopeLease(datasetId, opts ?? null, 'foreground_render')
    return this.bus.request('cache_query_sql', [datasetId ?? null, sql ?? null, workerOpts ?? null], { timeoutMs })
  }

  async cache_apply_template_prelude(
    datasetId: string,
    payload: {
      preludeDdl?: string[]
      preludeDml?: Array<{
        tableKey: string
        signature: string
        stmts: string[]
      }>
      plannerMetadata?: WorksheetPlannerMetadata | null
      timeoutMs?: number
      executionScopeId?: string | null
    } | null,
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(payload?.timeoutMs ?? 60000))
    const workerPayload = await this.withDefaultExecutionScopeLease(datasetId, payload ?? null, 'foreground_render')
    return this.bus.request('cache_apply_template_prelude', [datasetId ?? null, workerPayload ?? null], { timeoutMs })
  }

  async cache_interrupt_session(datasetId?: string | null, opts?: { timeoutMs?: number }): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 10000))
    return this.bus.request('cache_interrupt_session', [datasetId ?? null], {
      timeoutMs,
    })
  }

  async cache_start_download(
    datasetId?: string,
    tableName?: string,
    totalBytesEst?: number | string | null,
    parquetCompression?: string | null,
    rowLimit?: number | null,
  ): Promise<any> {
    return this.bus.request('cache_start_download', [
      datasetId ?? null,
      tableName ?? null,
      totalBytesEst ?? null,
      parquetCompression ?? null,
      rowLimit ?? null,
    ])
  }

  /**
   * Run dataset-less ad-hoc SQL in DuckDB (in-worker).
   * - Web: DuckDB-WASM
   * - Desktop (Tauri): native DuckDB via HOST_CALL
   *
   * This is used by Tableau parity + AI workflows that operate on Parquet/URLs
   * without a MotherDuck dataset id.
   */
  async duckdb_run_sql(
    sql: string,
    opts?: {
      previewRowLimit?: number
      preview_row_limit?: number
      maxRows?: number
      max_rows?: number
      allowMutating?: boolean
      allow_mutating?: boolean
      cacheExtracts?: boolean
      cache_extracts?: boolean
      maxCachedExtracts?: number
      max_cached_extracts?: number
      interruptPrevious?: boolean
      interrupt_previous?: boolean
      timeoutMs?: number
      duckdb_max_expression_depth?: number
      duckdbMaxExpressionDepth?: number
      persistentAdHoc?: boolean
      persistent_ad_hoc?: boolean
      persistentDatasetId?: string
      persistent_dataset_id?: string
      executionScopeId?: string | null
      execution_scope_id?: string | null
      tabExecutionOpfsPath?: string | null
      tab_execution_opfs_path?: string | null
      browserTabId?: string | null
      browser_tab_id?: string | null
      format?: 'ipc' | 'preview' | string
      returnIpc?: boolean
      return_ipc?: boolean
      ipcBufferAb?: boolean
      ipc_buffer_ab?: boolean
      fgIpcBufferAb?: boolean
      fg_ipc_buffer_ab?: boolean
      requestPriority?: string | null
      request_priority?: string | null
      fgLane?: 'background' | 'interactive' | string | null
      fg_lane?: 'background' | 'interactive' | string | null
      // Optional debug tag to attribute SQL to a caller (used for parity/dev instrumentation)
      caller?: string
    },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    const caller = String((opts as any)?.caller ?? '').trim()
    const workerOpts: any = caller.startsWith('QuickBooksCfoCore:')
      ? await this.withRequiredCfoCoreTabExecutionScope(opts ?? null, 'cfo_core_duckdb')
      : opts
        ? { ...(opts as any) }
        : null
    if (workerOpts && workerOpts.timeoutMs !== undefined) delete workerOpts.timeoutMs
    if (workerOpts && caller.startsWith('QuickBooksCfoCore:')) {
      workerOpts.persistentAdHoc = true
      workerOpts.persistentDatasetId = workerOpts.persistentDatasetId || workerOpts.persistent_dataset_id || CFO_CORE_DUCKDB_DATASET_ID
    }
    if (shouldDumpSqlToLogs()) {
      logSqlDumpBlock('duckdb_run_sql', String(sql ?? ''), {
        method: 'duckdb_run_sql',
        caller: (opts as any)?.caller ?? null,
        timeoutMs,
        previewRowLimit: (opts as any)?.previewRowLimit ?? (opts as any)?.preview_row_limit ?? null,
      })
    }
    return this.bus.request('duckdb_run_sql', [sql, workerOpts], { timeoutMs })
  }

  async duckdb_query_parquet_urls(
    urls: string[],
    opts?: {
      previewRowLimit?: number
      preview_row_limit?: number
      timeoutMs?: number
      caller?: string
      tableId?: string
      table_id?: string
    },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 180000))
    const workerOpts: any = opts ? { ...(opts as any) } : null
    if (workerOpts && workerOpts.timeoutMs !== undefined) delete workerOpts.timeoutMs
    return this.bus.request('duckdb_query_parquet_urls', [urls, workerOpts], {
      timeoutMs,
    })
  }

  async duckdb_create_table_from_parquet_urls(
    tableName: string,
    urls: string[],
    opts?: {
      persistent?: boolean
      persistentTable?: boolean
      persistent_table?: boolean
      timeoutMs?: number
      caller?: string
      persistentDatasetId?: string
      persistent_dataset_id?: string
      executionScopeId?: string | null
      execution_scope_id?: string | null
      tabExecutionOpfsPath?: string | null
      tab_execution_opfs_path?: string | null
      browserTabId?: string | null
      browser_tab_id?: string | null
      sharedPartCacheKey?: string | null
      shared_part_cache_key?: string | null
      partBytes?: number[] | null
      part_bytes?: number[] | null
      smallPartBufferThresholdBytes?: number | null
      small_part_buffer_threshold_bytes?: number | null
      requestPriority?: string | null
      request_priority?: string | null
      fgLane?: 'background' | 'interactive' | string | null
      fg_lane?: 'background' | 'interactive' | string | null
    },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 900000))
    const caller = String((opts as any)?.caller ?? '').trim()
    const workerOpts: any = caller.startsWith('QuickBooksCfoCore:')
      ? await this.withRequiredCfoCoreTabExecutionScope(opts ?? null, 'cfo_core_duckdb')
      : opts
        ? { ...(opts as any) }
        : null
    if (workerOpts && workerOpts.timeoutMs !== undefined) delete workerOpts.timeoutMs
    return this.bus.request('duckdb_create_table_from_parquet_urls', [tableName, urls, workerOpts], { timeoutMs })
  }

  /**
   * Run a multi-statement DuckDB plan (temp tables) in one session.
   */
  async duckdb_run_plan(
    plan: { statements: string[]; finalSql: string; cleanup?: string[] },
    opts?: {
      previewRowLimit?: number
      preview_row_limit?: number
      timeoutMs?: number
      caller?: string
    },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    const workerOpts: any = opts ? { ...(opts as any) } : null
    if (workerOpts && workerOpts.timeoutMs !== undefined) delete workerOpts.timeoutMs
    if (shouldDumpSqlToLogs()) {
      try {
        const stmts: string[] = Array.isArray((plan as any)?.statements) ? (plan as any).statements : []
        const cleanup: string[] = Array.isArray((plan as any)?.cleanup) ? (plan as any).cleanup : []
        const finalSql = String((plan as any)?.finalSql ?? '')
        const body = [
          `-- statements (${stmts.length})`,
          ...stmts.map((s: any, i: number) => `-- statement[${i}]\n${String(s ?? '')}`),
          `-- finalSql`,
          finalSql,
          cleanup.length ? `-- cleanup (${cleanup.length})` : '',
          ...cleanup.map((s: any, i: number) => `-- cleanup[${i}]\n${String(s ?? '')}`),
        ]
          .filter((x) => String(x ?? '').trim().length > 0)
          .join('\n\n')
        logSqlDumpBlock('duckdb_run_plan', body, {
          method: 'duckdb_run_plan',
          caller: (opts as any)?.caller ?? null,
          timeoutMs,
          previewRowLimit: (opts as any)?.previewRowLimit ?? (opts as any)?.preview_row_limit ?? null,
          statements: stmts.length,
          cleanup: cleanup.length,
          finalSqlLength: finalSql.length,
        })
      } catch {}
    }
    return this.bus.request('duckdb_run_plan', [plan, workerOpts], {
      timeoutMs,
    })
  }

  async duckdb_create_temp_table_from_rows(
    tableName: string,
    columns: Array<string | { name: string; type?: string | null }>,
    rows: unknown[],
    opts?: {
      maxRows?: number
      max_rows?: number
      rowLimit?: number
      row_limit?: number
      chunkSize?: number
      chunk_size?: number
      dropIfExists?: boolean
      drop_if_exists?: boolean
      persistent?: boolean
      persistentTable?: boolean
      persistent_table?: boolean
      persistentDatasetId?: string
      persistent_dataset_id?: string
      executionScopeId?: string | null
      execution_scope_id?: string | null
      tabExecutionOpfsPath?: string | null
      tab_execution_opfs_path?: string | null
      browserTabId?: string | null
      browser_tab_id?: string | null
      caller?: string
      requestPriority?: string | null
      request_priority?: string | null
      fgLane?: 'background' | 'interactive' | string | null
      fg_lane?: 'background' | 'interactive' | string | null
      timeoutMs?: number
    },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 180000))
    const caller = String((opts as any)?.caller ?? '').trim()
    const workerOpts: any = caller.startsWith('QuickBooksCfoCore:')
      ? await this.withRequiredCfoCoreTabExecutionScope(opts ?? null, 'cfo_core_duckdb')
      : opts
        ? { ...(opts as any) }
        : null
    if (workerOpts && workerOpts.timeoutMs !== undefined) delete workerOpts.timeoutMs
    return this.bus.request('duckdb_create_temp_table_from_rows', [tableName, columns, rows, workerOpts], { timeoutMs })
  }

  async ai_materialize_session(
    req: {
      session_key: string
      entity_key?: string | null
      source:
        | { kind: 'worksheet_sql'; sql: string }
        | { kind: 'parquet_uri'; parquet_uri: string; sql?: string | null }
        | { kind: 'oracle_rows'; columns: string[]; rows: unknown[][] }
      filters?: Array<{
        field_name: string
        type?: string
        value?: any
      }> | null
      selected_fields?: any | null
      field_dictionary?: any | null
      options?: {
        max_base_rows?: number
        maxBaseRows?: number
        drop_if_exists?: boolean
        dropIfExists?: boolean
      }
      // Optional execution-scoped dataset binding. When set, the session's
      // base/tidy tables are materialized inside the dataset's tab-local
      // DuckDB cache (e.g. CFO Core `__fg_cfo_core_duckdb__` under a `tab:`
      // scope) so the session SQL can read the dataset's live tables and the
      // ai_tool_* RPCs query that same live cache.
      dataset_id?: string | null
      datasetId?: string | null
      execution_scope_id?: string | null
      executionScopeId?: string | null
      tab_execution_opfs_path?: string | null
      tabExecutionOpfsPath?: string | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 180000))
    const scopeId = String(req?.execution_scope_id ?? req?.executionScopeId ?? '').trim()
    const datasetId = String(req?.dataset_id ?? req?.datasetId ?? '').trim()
    // Reuse the CFO Core tab-execution lease path so the worker opens the same
    // tab-local OPFS DuckDB file the dataset query path uses.
    const workerReq =
      datasetId && scopeId.startsWith('tab:')
        ? await this.withRequiredCfoCoreTabExecutionScope({ ...req, persistentDatasetId: datasetId }, 'cfo_core_duckdb')
        : req
    return this.bus.request('ai_materialize_session', [workerReq], {
      timeoutMs,
    })
  }

  async ai_run_insights_battery(
    req: {
      session_key: string
      max_queries?: number
      maxQueries?: number
      preview_row_limit?: number
      previewRowLimit?: number
    },
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 180000))
    return this.bus.request('ai_run_insights_battery', [req], { timeoutMs })
  }

  async ai_tool_groupby(
    req: {
      session_key: string
      group_by: string[]
      measure?: string | null
      agg?: string | null
      limit?: number | null
      preview_row_limit?: number | null
      previewRowLimit?: number | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    return this.bus.request('ai_tool_groupby', [req], { timeoutMs })
  }

  async ai_tool_rows(
    req: {
      session_key: string
      columns: string[]
      where?: { column: string; op?: string | null; value: any } | null
      limit?: number | null
      preview_row_limit?: number | null
      previewRowLimit?: number | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    return this.bus.request('ai_tool_rows', [req], { timeoutMs })
  }

  async ai_tool_compare_periods(
    req: {
      session_key: string
      period_field: string
      period_a: string
      period_b: string
      group_by?: string[] | null
      measure?: string | null
      agg?: string | null
      limit?: number | null
      preview_row_limit?: number | null
      previewRowLimit?: number | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    return this.bus.request('ai_tool_compare_periods', [req], { timeoutMs })
  }

  async ai_tool_compare_segments(
    req: {
      session_key: string
      segment_field: string
      segment_a: string
      segment_b: string
      group_by?: string[] | null
      measure?: string | null
      agg?: string | null
      limit?: number | null
      preview_row_limit?: number | null
      previewRowLimit?: number | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    return this.bus.request('ai_tool_compare_segments', [req], { timeoutMs })
  }

  async ai_tool_explain_outliers(
    req: {
      session_key: string
      measure: string
      by?: string | null
      limit?: number | null
      preview_row_limit?: number | null
      previewRowLimit?: number | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    return this.bus.request('ai_tool_explain_outliers', [req], { timeoutMs })
  }

  async ai_tool_histogram(
    req: {
      session_key: string
      measure: string
      bins?: number | null
      preview_row_limit?: number | null
      previewRowLimit?: number | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    return this.bus.request('ai_tool_histogram', [req], { timeoutMs })
  }

  async ai_tool_correlation(
    req: {
      session_key: string
      x: string
      y: string
      preview_row_limit?: number | null
      previewRowLimit?: number | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    return this.bus.request('ai_tool_correlation', [req], { timeoutMs })
  }

  async ai_tool_topk(
    req: {
      session_key: string
      columns: string[]
      order_by: string
      order_dir?: string | null
      where?: { column: string; op?: string | null; value: any } | null
      limit?: number | null
      preview_row_limit?: number | null
      previewRowLimit?: number | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    return this.bus.request('ai_tool_topk', [req], { timeoutMs })
  }

  async cache_run_sql(
    datasetId: string | null,
    sql: string,
    route: 'auto' | 'local' | 'remote' = 'auto',
    opts?: {
      timeoutMs?: number
      previewRowLimit?: number
      preview_row_limit?: number
      bindParams?: Array<{
        type: 'null' | 'boolean' | 'number' | 'string'
        value?: unknown
      }>
      bind_params?: Array<{
        type: 'null' | 'boolean' | 'number' | 'string'
        value?: unknown
      }>
      execPlan?: { statements: string[]; finalSql: string; cleanup?: string[] }
      exec_plan?: {
        statements: string[]
        finalSql: string
        cleanup?: string[]
      }
      plannerMetadata?: {
        authoringRevisionId?: string | null
        semanticPlanSignature?: string | null
        physicalPlanSignature?: string | null
        bindingGeneration?: string | null
        budgetClass?: string | null
      } | null
      physicalPlanSignature?: string | null
      bindingGeneration?: string | null
      requestPriority?: string | null
      request_priority?: string | null
      skipDefaultExecutionScope?: boolean | null
      skip_default_execution_scope?: boolean | null
      duckdb_max_expression_depth?: number | null
      duckdbMaxExpressionDepth?: number | null
    },
  ): Promise<any> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000))
    const workerOpts: any =
      (await this.withDefaultExecutionScopeLease(
        datasetId,
        opts ?? null,
        String((opts as any)?.requestPriority ?? (opts as any)?.request_priority ?? '').trim() ||
          ((opts as any)?.execPlan || (opts as any)?.exec_plan ? 'foreground_render' : null),
      )) ?? {}
    const hasExecPlan =
      !!(workerOpts?.exec_plan && typeof workerOpts.exec_plan === 'object') ||
      !!(workerOpts?.execPlan && typeof workerOpts.execPlan === 'object')
    const directNativeRequested = (() => {
      const raw = workerOpts?.fg_renderer_native_direct ?? workerOpts?.fgRendererNativeDirect
      if (raw === undefined || raw === null) return false
      const s = String(raw).trim().toLowerCase()
      return s === '1' || s === 'true' || s === 'yes' || s === 'on'
    })()
    if (workerOpts && typeof workerOpts === 'object') {
      delete workerOpts.fg_renderer_native_direct
      delete workerOpts.fgRendererNativeDirect
    }
    if (shouldDumpSqlToLogs()) {
      logSqlDumpBlock('cache_run_sql', String(sql ?? ''), {
        method: 'cache_run_sql',
        datasetId: datasetId ?? null,
        route,
        timeoutMs,
        previewRowLimit: (workerOpts as any)?.previewRowLimit ?? (workerOpts as any)?.preview_row_limit ?? null,
      })
    }
    if (directNativeRequested && !hasExecPlan && route === 'local' && datasetId) {
      try {
        return await this.runDirectNativeArrowQuery(
          String(datasetId),
          String(sql ?? ''),
          Array.isArray(workerOpts?.bind_params)
            ? workerOpts.bind_params
            : Array.isArray(workerOpts?.bindParams)
              ? workerOpts.bindParams
              : [],
        )
      } catch (e: any) {
        // Keep existing worker path as a safety net while this optimization is validated.
        console.warn('[AppWorkerClient] direct-native cache_run_sql fallback to worker path', e)
      }
    }
    const nextOpts = workerOpts && typeof workerOpts === 'object' && Object.keys(workerOpts).length > 0 ? workerOpts : null
    const nowPerf = (): number =>
      typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now()
    const requestStartedPerf = nowPerf()
    const timerProbeIntervalMs = 50
    let timerProbeTickCount = 0
    let timerProbeMaxDriftMs = 0
    let timerProbeFirstTickPerfMs: number | null = null
    let timerProbeExpectedPerfMs = requestStartedPerf + timerProbeIntervalMs
    const timerProbeId = setInterval(() => {
      const now = nowPerf()
      timerProbeTickCount += 1
      if (timerProbeFirstTickPerfMs === null) timerProbeFirstTickPerfMs = now
      const driftMs = Math.max(0, now - timerProbeExpectedPerfMs)
      if (driftMs > timerProbeMaxDriftMs) timerProbeMaxDriftMs = driftMs
      timerProbeExpectedPerfMs = now + timerProbeIntervalMs
    }, timerProbeIntervalMs)
    try {
      const out = await this.bus.request('cache_run_sql', [datasetId ?? null, sql, route, nextOpts], { timeoutMs })
      if (out && typeof out === 'object') {
        const localPerf = (out as any).local_perf
        if (localPerf && typeof localPerf === 'object') {
          const ipcRaw = (out as any).ipc_buffer
          let hasIpcBuffer = false
          let ipcBufferBytes: number | null = null
          let ipcBufferType: string | null = null
          if (ipcRaw instanceof Uint8Array) {
            hasIpcBuffer = ipcRaw.byteLength > 0
            ipcBufferBytes = ipcRaw.byteLength
            ipcBufferType = 'Uint8Array'
          } else if (ipcRaw instanceof ArrayBuffer) {
            hasIpcBuffer = ipcRaw.byteLength > 0
            ipcBufferBytes = ipcRaw.byteLength
            ipcBufferType = 'ArrayBuffer'
          } else if (ipcRaw != null) {
            hasIpcBuffer = true
            ipcBufferType = typeof ipcRaw
          }
          ;(localPerf as any).app_client_request_wall_ms = Math.max(0, nowPerf() - requestStartedPerf)
          ;(localPerf as any).app_client_timer_probe_tick_count = timerProbeTickCount
          ;(localPerf as any).app_client_timer_probe_max_drift_ms = timerProbeMaxDriftMs
          ;(localPerf as any).app_client_timer_probe_first_tick_ms =
            timerProbeFirstTickPerfMs !== null && Number.isFinite(timerProbeFirstTickPerfMs)
              ? Math.max(0, Number(timerProbeFirstTickPerfMs) - (requestStartedPerf + timerProbeIntervalMs))
              : null
          ;(localPerf as any).app_client_timer_probe_pending_at_resolve = timerProbeTickCount <= 0
          ;(localPerf as any).app_client_received_has_ipc_buffer = hasIpcBuffer
          ;(localPerf as any).app_client_received_ipc_buffer_bytes =
            ipcBufferBytes !== null && Number.isFinite(Number(ipcBufferBytes)) ? Number(ipcBufferBytes) : null
          ;(localPerf as any).app_client_received_ipc_buffer_type = ipcBufferType
        }
      }
      return out
    } finally {
      clearInterval(timerProbeId)
    }
  }

  async cache_clear(datasetId?: string): Promise<any> {
    return this.bus.request('cache_clear', [datasetId ?? null])
  }

  async cache_cleanup_zero_byte_files(opts?: { maxDepth?: number }): Promise<any> {
    return this.bus.request('cache_cleanup_zero_byte_files', [opts ?? {}], {
      timeoutMs: 60000,
    })
  }

  async cache_finalize_parquet(datasetId?: string): Promise<any> {
    return this.bus.request('cache_finalize_parquet', [datasetId ?? null])
  }

  async cache_warm_local(datasetId: string): Promise<any> {
    return this.bus.request('cache_warm_local', [datasetId], {
      timeoutMs: 180000,
    })
  }

  async cache_rebuild_rw(datasetId: string): Promise<any> {
    return this.bus.request('cache_rebuild_rw', [datasetId], {
      timeoutMs: 600000,
    })
  }

  async cache_prune_storage(opts?: { budgetBytes?: number; activeDatasetIds?: string[] }): Promise<any> {
    return this.bus.request('cache_prune_storage', [opts ?? null], {
      timeoutMs: 180000,
    })
  }

  async cache_clear_browser_storage(opts?: { writeEpochMarker?: boolean; write_epoch_marker?: boolean }): Promise<any> {
    return this.bus.request('cache_clear_browser_storage', [opts ?? null], {
      timeoutMs: 180000,
    })
  }

  async cache_set_browser_durable_cache_budget(opts?: { budgetBytes?: number | string }): Promise<any> {
    return this.bus.request('cache_set_browser_durable_cache_budget', [opts ?? null], { timeoutMs: 10000 })
  }

  async cache_cleanup_orphan_parquet(opts?: { maxDepth?: number }): Promise<any> {
    return this.bus.request('cache_cleanup_orphan_parquet', [opts ?? null], {
      timeoutMs: 180000,
    })
  }

  async cache_ingest_published_extract(
    datasetId: string,
    url: string,
    meta?: { extract_version?: string | number; etag?: string; bytes?: number },
  ): Promise<any> {
    return this.bus.request('cache_ingest_published_extract', [datasetId, url, meta ?? null], { timeoutMs: 600000 })
  }

  async cache_ingest_published_extract_parts(
    datasetId: string,
    urls: string[],
    meta?: {
      extract_version?: string | number
      etag?: string
      bytes?: number
      table_id?: string
      row_count_hint?: number
    },
  ): Promise<any> {
    return this.bus.request('cache_ingest_published_extract_parts', [datasetId, urls, meta ?? null], { timeoutMs: 900000 })
  }

  async cache_prepare_dataset(
    datasetId: string,
    payload?: {
      source?: {
        url?: string | null
        extract_uri?: string | null
        extract_version?: string | number | null
        extract_identity?: string | null
        etag?: string | null
        bytes?: number | null
      } | null
      semanticSpec?: any
      semantic_spec?: any
      requireSemantic?: boolean
      prefer?: 'table' | 'view'
      datasourceKey?: string | null
      datasource_key?: string | null
      duckdb_threads?: number
      requireWrite?: boolean
      forceRefresh?: boolean
      refresh?: boolean
      deferMaintenance?: boolean
      sharedBaseBuild?: boolean
      forceImmediateMaintenance?: boolean
      executionScopeId?: string | null
      compactManifest?: boolean
    } | null,
  ): Promise<any> {
    const workerPayload = await this.withDefaultExecutionScopeLease(datasetId, payload ?? null, 'foreground_render')
    return await this.withWorkbookArtifactLock(
      datasetId,
      workerPayload as any,
      async () => await this.bus.request('cache_prepare_dataset', [datasetId, workerPayload], { timeoutMs: 600000 }),
    )
  }

  async cache_ensure_working_set(payload: {
    datasetId: string
    signature: string
    kind?: 'context' | 'base'
    baseTable?: string | null
    whereSql?: string | null
    preludeStatements?: string[] | null
    maxTables?: number | null
    duckdb_threads?: number | null
    duckdb_object_cache?: boolean | null
    duckdb_memory_limit?: string | null
    duckdb_temp_directory?: string | null
    duckdb_max_expression_depth?: number | null
    duckdb_preserve_insertion_order?: boolean | null
    executionScopeId?: string | null
    skipDefaultExecutionScope?: boolean | null
    bindingGeneration?: string | null
    physicalPlanSignature?: string | null
  }): Promise<any> {
    const datasetId = String(payload?.datasetId ?? '').trim()
    if (!datasetId) throw new Error('cache_ensure_working_set: missing datasetId')
    const explicitScopeId = String((payload as any)?.executionScopeId ?? (payload as any)?.execution_scope_id ?? '').trim()
    const whereSqlRaw = String((payload as any)?.whereSql ?? (payload as any)?.where_sql ?? '').trim()
    const whereSql = isNonRestrictiveWhereSql(whereSqlRaw) ? '' : whereSqlRaw
    const preludeStatements = Array.isArray((payload as any)?.preludeStatements ?? (payload as any)?.prelude_statements)
      ? ((payload as any)?.preludeStatements ?? (payload as any)?.prelude_statements)
      : []
    const skipDefaultScope =
      Boolean((payload as any)?.skipDefaultExecutionScope === true) ||
      Boolean((payload as any)?.skip_default_execution_scope === true) ||
      (!explicitScopeId && !whereSql && preludeStatements.length === 0)
    const workerPayload = skipDefaultScope
      ? { ...(payload as any) }
      : await this.withDefaultExecutionScopeLease(datasetId, payload ?? null, 'foreground_render')
    return this.bus.request('cache_ensure_working_set', [datasetId, workerPayload], { timeoutMs: 180000 })
  }

  async cache_ensure_persisted_shared_base(payload: {
    datasetId: string
    extractIdentity?: string | null
    semanticSpecHash?: string | null
    workingSetSignature?: string | null
    aggBaseSignature: string
    sql: string
    preludeStatements?: string[] | null
    maxTables?: number | null
    buildIfMissing?: boolean | null
    duckdb_threads?: number | null
    duckdb_object_cache?: boolean | null
    duckdb_memory_limit?: string | null
    duckdb_temp_directory?: string | null
    duckdb_max_expression_depth?: number | null
    duckdb_preserve_insertion_order?: boolean | null
    executionScopeId?: string | null
  }): Promise<any> {
    const datasetId = String(payload?.datasetId ?? '').trim()
    if (!datasetId) throw new Error('cache_ensure_persisted_shared_base: missing datasetId')
    const workerPayload = await this.withDefaultExecutionScopeLease(datasetId, payload ?? null, 'workbook_warmup')
    return this.bus.request('cache_ensure_persisted_shared_base', [datasetId, workerPayload], { timeoutMs: 300000 })
  }

  async cache_ensure_datasource_node(payload: {
    datasetId: string
    nodeKind:
      | 'semantic_base'
      | 'working_set'
      | 'aggregate_base'
      | 'window_input_base'
      | 'worksheet_presentation'
      | 'worksheet_interaction_cube'
    nodeSignature: string
    sql?: string | null
    sourceTableName?: string | null
    preludeStatements?: string[] | null
    extractIdentity?: string | null
    semanticSpecHash?: string | null
    workingSetSignature?: string | null
    grainSignature?: string | null
    measureSetSignature?: string | null
    calcParamSignature?: string | null
    windowInputSignature?: string | null
    executionSignature?: string | null
    skipDefaultExecutionScope?: boolean | null
    forceDurableDatasourceNode?: boolean | null
    workbookArtifactWarmup?: boolean | null
    requestPriority?: string | null
    /**
     * Phase 2: client-computed canonical root identity key. When supplied the worker
     * validates the client/worker views agree (hit/miss telemetry) and uses the
     * worker-resolved value for manifest entries. Clients should derive this via
     * `buildCanonicalRootIdentityKey({ datasetId, extractIdentity, semanticSpecHash })`.
     */
    canonicalRootKey?: string | null
    maxTables?: number | null
    buildIfMissing?: boolean | null
    duckdb_threads?: number | null
    duckdb_object_cache?: boolean | null
    duckdb_memory_limit?: string | null
    duckdb_temp_directory?: string | null
    duckdb_max_expression_depth?: number | null
    duckdb_preserve_insertion_order?: boolean | null
    executionScopeId?: string | null
  }): Promise<any> {
    const datasetId = String(payload?.datasetId ?? '').trim()
    if (!datasetId) throw new Error('cache_ensure_datasource_node: missing datasetId')
    const skipDefaultScope =
      Boolean((payload as any)?.skipDefaultExecutionScope === true) ||
      Boolean((payload as any)?.skip_default_execution_scope === true) ||
      Boolean((payload as any)?.forceDurableDatasourceNode === true) ||
      Boolean((payload as any)?.force_durable_datasource_node === true) ||
      Boolean((payload as any)?.workbookArtifactWarmup === true) ||
      Boolean((payload as any)?.workbook_artifact_warmup === true)
    const workerPayload = skipDefaultScope
      ? { ...(payload as any) }
      : await this.withDefaultExecutionScopeLease(datasetId, payload ?? null, null)
    // Phase 2: auto-derive the canonical root identity on the client so worker telemetry
    // can emit `canonical_root_hit` / `canonical_root_miss` signals without every call
    // site having to wire the key manually. Callers that explicitly pass a
    // `canonicalRootKey` (for example, tests) retain their override.
    const existingCanonicalRootKey = (() => {
      const direct = String((workerPayload as any)?.canonicalRootKey ?? (workerPayload as any)?.canonical_root_key ?? '').trim()
      return direct || null
    })()
    if (!existingCanonicalRootKey) {
      const derived = buildCanonicalRootIdentityKey({
        datasetId,
        extractIdentity: (workerPayload as any)?.extractIdentity ?? (workerPayload as any)?.extract_identity ?? null,
        semanticSpecHash: (workerPayload as any)?.semanticSpecHash ?? (workerPayload as any)?.semantic_spec_hash ?? null,
      })
      if (derived) {
        ;(workerPayload as any).canonicalRootKey = derived
      }
    }
    return await this.withWorkbookArtifactLock(
      datasetId,
      workerPayload as any,
      async () => await this.bus.request('cache_ensure_datasource_node', [datasetId, workerPayload], { timeoutMs: 300000 }),
    )
  }

  async cache_ensure_interaction_cube(payload: {
    datasetId: string
    cubeSignature: string
    sourceTableName?: string | null
    sourceSql?: string | null
    executionScopeId?: string | null
    contractVersion?: number | null
    cubeKind?: 'crosstab' | 'mark' | 'detail' | string | null
    axisFields?: Array<{
      fieldName?: string | null
      displayName?: string | null
      columnName: string
      role?: string | null
    }> | null
    measureComponents?: Array<{
      fieldName?: string | null
      displayName?: string | null
      columnName: string
      aggregate?: string | null
      resultColumnName?: string | null
    }> | null
    slicerFields?: Array<{
      canonicalFieldKey?: string | null
      displayName?: string | null
      fieldName?: string | null
      calculationToken?: string | null
      columnName: string
      domainMode?: 'all' | 'relevant' | 'context' | string | null
      isCalculated?: boolean | null
    }> | null
    worksheetName?: string | null
    workbookId?: string | null
    extractIdentity?: string | null
    semanticSpecHash?: string | null
    physicalPlanSignature?: string | null
    worksheetGrainSignature?: string | null
    measureSetSignature?: string | null
    parameterSignature?: string | null
    sourceNodeSignature?: string | null
    authoringRevisionId?: string | null
    applySqlTemplate?: string | null
    applySourceRelationName?: string | null
    unsupportedReason?: string | null
    maxCubes?: number | null
    buildIfMissing?: boolean | null
    forceDurableInteractionCube?: boolean | null
    skipDefaultExecutionScope?: boolean | null
    duckdb_threads?: number | null
    duckdb_object_cache?: boolean | null
    duckdb_memory_limit?: string | null
    duckdb_temp_directory?: string | null
    duckdb_max_expression_depth?: number | null
    duckdb_preserve_insertion_order?: boolean | null
  }): Promise<any> {
    const datasetId = String(payload?.datasetId ?? '').trim()
    if (!datasetId) throw new Error('cache_ensure_interaction_cube: missing datasetId')
    const workerPayload = await this.withDefaultExecutionScopeLease(datasetId, payload ?? null, 'cube_build')
    return this.bus.request('cache_ensure_interaction_cube', [datasetId, workerPayload], { timeoutMs: 300000 })
  }

  async cache_query_filter_domain(payload: {
    datasetId: string
    cubeSignature?: string | null
    canonicalFieldKey?: string | null
    calculationToken?: string | null
    fieldName?: string | null
    displayName?: string | null
    columnName: string
    domainMode?: 'all' | 'relevant' | 'context' | string | null
    slicerFields?: Array<Record<string, any>> | null
    otherFilters?: Array<Record<string, any>> | null
    executionScopeId?: string | null
    limit?: number | null
  }): Promise<any> {
    const datasetId = String(payload?.datasetId ?? '').trim()
    if (!datasetId) throw new Error('cache_query_filter_domain: missing datasetId')
    const workerPayload = await this.withDefaultExecutionScopeLease(datasetId, payload ?? null, 'active_domain_query')
    return this.bus.request('cache_query_filter_domain', [datasetId, workerPayload], { timeoutMs: 60000 })
  }

  async cache_query_filter_domains(payload: {
    datasetId: string
    cubeSignature?: string | null
    executionScopeId?: string | null
    domains: Array<{
      canonicalFieldKey?: string | null
      calculationToken?: string | null
      fieldName?: string | null
      displayName?: string | null
      columnName: string
      domainMode?: 'all' | 'relevant' | 'context' | string | null
      otherFilters?: Array<Record<string, any>> | null
      limit?: number | null
    }>
    slicerFields?: Array<Record<string, any>> | null
  }): Promise<any> {
    const datasetId = String(payload?.datasetId ?? '').trim()
    if (!datasetId) throw new Error('cache_query_filter_domains: missing datasetId')
    const workerPayload = await this.withDefaultExecutionScopeLease(datasetId, payload ?? null, 'active_domain_query')
    return this.bus.request('cache_query_filter_domains', [datasetId, workerPayload], { timeoutMs: 60000 })
  }

  async cache_query_interaction_cube(payload: {
    datasetId: string
    cubeSignature?: string | null
    cubeKind?: 'crosstab' | 'mark' | 'detail' | string | null
    worksheetName?: string | null
    filters?: Array<Record<string, any>> | null
    slicerFields?: Array<Record<string, any>> | null
    axisFields?: Array<Record<string, any>> | null
    measureComponents?: Array<Record<string, any>> | null
    applySqlTemplate?: string | null
    applySourceRelationName?: string | null
    executionScopeId?: string | null
    limit?: number | null
    offset?: number | null
    skipDefaultExecutionScope?: boolean | null
  }): Promise<any> {
    const datasetId = String(payload?.datasetId ?? '').trim()
    if (!datasetId) throw new Error('cache_query_interaction_cube: missing datasetId')
    const workerPayload = await this.withDefaultExecutionScopeLease(datasetId, payload ?? null, 'active_cube_query')
    return this.bus.request('cache_query_interaction_cube', [datasetId, workerPayload], { timeoutMs: 60000 })
  }

  async cache_reset_execution_scope(
    datasetId: string,
    payload?: {
      executionScopeId?: string | null
    } | null,
  ): Promise<any> {
    const datasetIdNorm = String(datasetId ?? '').trim()
    if (!datasetIdNorm) throw new Error('cache_reset_execution_scope: missing datasetId')
    const workerPayload = await this.withDefaultExecutionScopeLease(datasetIdNorm, payload ?? null, 'foreground_render')
    return this.bus.request('cache_reset_execution_scope', [datasetIdNorm, workerPayload], { timeoutMs: 30000 })
  }

  // --- Client Semantic Layer ---

  async cache_set_semantic_spec(
    datasetId: string,
    payload: {
      datasourceKey: string
      bindingMap: any
      fieldManifest?: any | null
      includeCalcs?: boolean
      prefer?: 'table' | 'view'
      orderBy?: string[] | null
    },
  ): Promise<any> {
    return this.bus.request('cache_set_semantic_spec', [datasetId, payload], {
      timeoutMs: 60000,
    })
  }

  async cache_set_semantic_spec_direct(datasetId: string, spec: any): Promise<any> {
    return this.bus.request('cache_set_semantic_spec_direct', [datasetId, { spec }], { timeoutMs: 60000 })
  }

  async cache_build_semantic_layer(datasetId: string, opts?: { force?: boolean }): Promise<any> {
    return this.bus.request('cache_build_semantic_layer', [datasetId, opts ?? null], { timeoutMs: 180000 })
  }

  async get_local_table_columns(tableName: string): Promise<{
    columns: Array<{ name: string; type: string }>
    table_schema: string
    table_name: string
  }> {
    return this.bus.request('get_local_table_columns', [tableName], {
      timeoutMs: 30000,
    })
  }

  async get_local_dataset_columns(datasetId: string): Promise<any> {
    const workerPayload = await this.withDefaultExecutionScopeLease(datasetId, null, 'foreground_render')
    return this.bus.request('get_local_dataset_columns', [datasetId, workerPayload ?? null], { timeoutMs: 30000 })
  }

  // --- Published Extracts (Tableau-like Query Tabs) ---

  async published_extract_download(url: string, opfsPath: string): Promise<any> {
    return this.bus.request('published_extract_download', [url, opfsPath], {
      timeoutMs: 600000,
    })
  }
}
